import React, { useEffect, useMemo, useRef, useState, useDeferredValue, useCallback } from 'react';
import { View, Text, TextInput, TouchableOpacity, ScrollView, StyleSheet, Dimensions, Platform, Image, ActivityIndicator, Alert } from 'react-native';
import { Picker } from '@react-native-picker/picker';
import { Ionicons } from '@expo/vector-icons';
import Footer from '../components/Footer';
import {
  formatCurrency,
  formatNumber,
  validateRequired,
  validateEmail,
  validatePAN,
  validateISODate,
  isBeforeOrEqualDate,
  validateGSTIN,
  validatePostalCode,
  validateHSN,
  validateIFSC,
  validateUPI,
  validateNumber,
} from '../utils/formatters';
import countries from '../constants/countries';
import { useAuth } from '../contexts/AuthContext';
import { supabase, SUPABASE_URL, SUPABASE_ANON_KEY } from '../utils/supabaseClient';
import { useRoute, useNavigation } from '@react-navigation/native';

const { width: screenWidth } = Dimensions.get('window');
const isWeb = Platform.OS === 'web';

const currencySymbols = { INR: '₹', USD: '$', EUR: '€' };

// Stable SectionCard component (defined at module scope to avoid remounts)
const SectionCard = React.memo(({ title, children, right }) => (
  <View style={[styles.card, { width: '100%' }]}>
    <View style={styles.cardHeader}>
      <Text style={styles.cardTitle}>{title}</Text>
      {right}
    </View>
    {children}
  </View>
));

const INVOICE_TABLE = 'invoice_generator';

const InvoiceGeneratorScreen = () => {
  const route = useRoute();
  const navigation = useNavigation();
  const [screenData, setScreenData] = useState(Dimensions.get('window'));
  const isMobile = screenData.width < 900;

  // Layout shares between generator (left) and live preview (right)
  // Set both sides to 40% on desktop, full width on mobile
  const leftPanelShare = isMobile ? 1 : 0.6
  const rightPanelShare = isMobile ? 1 : 0.4

  // Scale the live preview so it fits the right card without clipping
  const previewScale = useMemo(() => {
    // Layout paddings and gap
    const outerPadding = 30; // calculatorContainer horizontal padding
    const gapBetweenColumns = 24; // styles.calculatorRow gap
    const cardPadding = 16; // styles.card horizontal padding
    const borderFudge = 12; // extra space for borders/scrollbar to avoid right-side clipping
    const innerBuffer = 20; // ensure preview never touches card edge
    const pageWidth = 794; // print page width in px
    const shrinkFactor = 0.85; // render slightly smaller to fit comfortably in card

    const containerWidth = screenData.width - outerPadding * 2;
    const availableRowWidth = Math.max(0, containerWidth - gapBetweenColumns);
    const panelWidth = isMobile ? containerWidth : availableRowWidth * rightPanelShare;
    const availableInner = Math.max(0, panelWidth - cardPadding * 2 - borderFudge - innerBuffer);
    const scale = Math.max(0.3, Math.min((availableInner * shrinkFactor) / pageWidth, 1));
    return scale;
  }, [screenData.width, isMobile]);

  useEffect(() => {
    const sub = Dimensions.addEventListener('change', ({ window }) => setScreenData(window));
    return () => sub?.remove?.();
  }, []);

  const [invoice, setInvoice] = useState({
    title: 'Invoice',
    number: 'A00001',
    invoiceDate: new Date().toISOString().slice(0, 10),
    showDueDate: false,
    dueDate: '',
    currency: 'INR',
    gstType: 'CGST_SGST', // 'IGST' or 'CGST_SGST'
    showTotalWords: false,
  });

  const [seller, setSeller] = useState({
    country: 'India',
    businessName: '',
    gstin: '',
    email: '',
    pan: '',
    address: '',
    city: '',
    postalCode: '',
  });

  const [client, setClient] = useState({
    country: 'India',
    businessName: '',
    gstin: '',
    email: '',
    pan: '',
    address: '',
    city: '',
    postalCode: '',
  });

  const [showShipping, setShowShipping] = useState(false);
  const [shipping, setShipping] = useState({ address: '', city: '', postalCode: '' });

  const [items, setItems] = useState([
    { id: 1, name: '', hsn: '', gstRate: 18, qty: 1, rate: 1, description: '', showDescription: false, duplicateOf: null },
  ]);

  const [charges, setCharges] = useState({
    addlCharges: 0,
    discount: 0,
    hideTaxes: false,
    roundMode: 'none', // 'none' | 'round_up' | 'round_down'
    summarizeQty: false,
  });

  const [notes, setNotes] = useState('');
  const [terms, setTerms] = useState('');
  const [contactDetails, setContactDetails] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [payment, setPayment] = useState({ bankName: '', accountNo: '', ifsc: '', upiId: '' });
  const [logoUri, setLogoUri] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const [pendingSave, setPendingSave] = useState(false);
  const [template, setTemplate] = useState('classic');
  const [errors, setErrors] = useState({ invoice: {}, seller: {}, client: {}, shipping: {}, items: {}, payment: {} });
  const [editingInvoiceId, setEditingInvoiceId] = useState(null); // Track if editing existing invoice

  // Saved invoices dashboard state
  const [savedInvoices, setSavedInvoices] = useState([]);
  const [savedLoading, setSavedLoading] = useState(false);
  const [savedError, setSavedError] = useState('');

  const { session, openAuthModal } = useAuth();

  // Load by id when navigated from Dashboard; prehydrate instantly and fetch concurrently
  useEffect(() => {
    const loadFromParams = async () => {
      const id = route?.params?.loadId;
      const shouldDownload = !!route?.params?.download;
      const snapshot = route?.params?.rowSnapshot;
      if (!id) return;

      // Mark as editing an existing invoice
      setEditingInvoiceId(id);

      // 1) Prehydrate immediately from passed snapshot or local cache (no network)
      try {
        if (snapshot && typeof snapshot === 'object') {
          loadInvoiceFromRow(snapshot);
        } else if (Platform.OS === 'web') {
          const key = `invoice_prefetch_${id}`;
          try {
            const raw = window.localStorage.getItem(key);
            if (raw) {
              const cached = JSON.parse(raw);
              if (cached && typeof cached === 'object') {
                loadInvoiceFromRow(cached);
              }
            }
          } catch (_) { }
        }
      } catch (_) { }

      // 2) Fetch concurrently via SDK and PostgREST; apply whichever returns first
      const token = session?.access_token || session?.accessToken || null;
      const restUrl = `${SUPABASE_URL}/rest/v1/${INVOICE_TABLE}?id=eq.${id}&select=*`;
      const restHeaders = { apikey: SUPABASE_ANON_KEY, Accept: 'application/json' };
      if (token) restHeaders['Authorization'] = `Bearer ${token}`;

      const sdkPromise = supabase
        .from(INVOICE_TABLE)
        .select('*')
        .eq('id', id)
        .maybeSingle();

      const restPromise = (async () => {
        const resp = await fetch(restUrl, { headers: restHeaders });
        const text = await resp.text();
        try {
          const json = JSON.parse(text);
          return { data: Array.isArray(json) ? json[0] || null : null, error: resp.ok ? null : { message: `HTTP ${resp.status}` } };
        } catch (e) {
          return { data: null, error: { message: 'Invalid JSON from REST' } };
        }
      })();

      // Soft timeout to avoid lingering
      const timeoutPromise = new Promise((resolve) => setTimeout(() => resolve({ data: null, error: { message: 'Timed out', code: 'ETIMEOUT' } }), 6000));

      let winner = null;
      try {
        winner = await Promise.race([sdkPromise, restPromise, timeoutPromise]);
      } catch (e) {
        winner = null;
      }

      if (winner && winner.data) {
        loadInvoiceFromRow(winner.data);
      }

      // 3) Handle auto-download once state is applied
      if (shouldDownload) {
        await new Promise((r) => setTimeout(r, 150));
        await exportPDF();
      }
    };
    loadFromParams();
  }, [route?.params?.loadId, route?.params?.download, route?.params?.rowSnapshot]);
  const [selectedMonth, setSelectedMonth] = useState('all'); // 'YYYY-MM' | 'all'
  const [monthStats, setMonthStats] = useState({ totalCount: 0, paidCount: 0, dueCount: 0, totalAmount: 0 });

  // session obtained earlier to avoid temporal dead zone in hooks above

  const invoiceRef = useRef(null);

  const currencySymbol = currencySymbols[invoice.currency] || '₹';

  // Validation helpers and error state management
  const validateParty = (party) => {
    const e = {};
    if (!validateRequired(party.businessName)) e.businessName = 'Business name is required';
    if (party.email && !validateEmail(party.email)) e.email = 'Invalid email';
    if (party.pan && !validatePAN(party.pan)) e.pan = 'Invalid PAN';
    if (party.gstin && !validateGSTIN(party.gstin)) e.gstin = 'Invalid GSTIN';
    if (party.postalCode && !validatePostalCode(party.country, party.postalCode)) e.postalCode = 'Invalid postal code';
    return e;
  };

  const validateInvoiceMeta = (inv) => {
    const e = {};
    if (inv.invoiceDate && !validateISODate(inv.invoiceDate)) e.invoiceDate = 'Use YYYY-MM-DD';
    if (inv.showDueDate && inv.dueDate) {
      if (!validateISODate(inv.dueDate)) e.dueDate = 'Use YYYY-MM-DD';
      else if (!isBeforeOrEqualDate(inv.invoiceDate, inv.dueDate)) e.dueDate = 'Due date must be after invoice date';
    }
    return e;
  };

  const validateShipping = (s) => {
    const e = {};
    const countryHint = seller?.country || client?.country || 'India';
    if (s.postalCode && !validatePostalCode(countryHint, s.postalCode)) e.postalCode = 'Invalid postal code';
    return e;
  };

  const validatePaymentMeta = (p) => {
    const e = {};
    if (p.ifsc && !validateIFSC(p.ifsc)) e.ifsc = 'Invalid IFSC';
    if (p.upiId && !validateUPI(p.upiId)) e.upiId = 'Invalid UPI ID';
    return e;
  };

  const validateLine = (line) => {
    const e = {};
    if (!validateRequired(line.name)) e.name = 'Item name is required';
    if (line.hsn && !validateHSN(line.hsn)) e.hsn = 'Invalid HSN/SAC';
    const qtyNum = parseFloat(line.qty);
    const rateNum = parseFloat(line.rate);
    const gstNum = parseFloat(line.gstRate);
    if (!validateNumber(qtyNum)) e.qty = 'Qty must be a number';
    if (!validateNumber(rateNum)) e.rate = 'Rate must be a number';
    if (!validateNumber(gstNum) || gstNum < 0 || gstNum > 28) e.gstRate = 'GST must be 0–28';
    return e;
  };

  useEffect(() => {
    const next = {
      invoice: validateInvoiceMeta(invoice),
      seller: validateParty(seller),
      client: validateParty(client),
      shipping: validateShipping(shipping),
      items: {},
      payment: validatePaymentMeta(payment),
    };
    items.forEach((it) => { next.items[it.id] = validateLine(it); });
    setErrors(next);
  }, [invoice, seller, client, shipping, items, payment]);

  // SEO: set page title and meta description on web
  useEffect(() => {
    if (!isWeb) return;
    try {
      const title = 'Free Invoice Generator - Online Invoice Bill Maker | Modern Munshiji';
      const description = 'Modern Munshiji free online invoice generator helps you create, download and send professional invoices. Simply add invoice details, customize your invoice using templates and get paid faster. Modern Munshiji invoice generator is one of the easiest and simplest tools to create invoices for free.';
      document.title = title;
      let meta = document.querySelector('meta[name="description"]');
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute('name', 'description');
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', description);

      // Open Graph metadata for better sharing and SEO context
      const setOG = (property, content) => {
        let og = document.querySelector(`meta[property="${property}"]`);
        if (!og) {
          og = document.createElement('meta');
          og.setAttribute('property', property);
          document.head.appendChild(og);
        }
        og.setAttribute('content', content);
      };
      setOG('og:title', title);
      setOG('og:description', description);
      setOG('og:type', 'website');
      setOG('og:url', typeof window !== 'undefined' ? window.location.href : '');
      setOG('og:image', '/assets/Modern-Munishiji-Logo.png');

      // Basic FAQ structured data for SEO
      const faqSchema = {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: 'Is the Modern Munshiji invoice generator free?',
            acceptedAnswer: { '@type': 'Answer', text: 'Yes, it is completely free to create and download invoices.' }
          },
          {
            '@type': 'Question',
            name: 'Can I add GST (IGST/CGST/SGST) to items?',
            acceptedAnswer: { '@type': 'Answer', text: 'Yes, choose the tax type and set GST rates per item.' }
          },
          {
            '@type': 'Question',
            name: 'Can I upload a company logo?',
            acceptedAnswer: { '@type': 'Answer', text: 'Yes, use the logo picker in Invoice Details to upload your logo.' }
          },
          {
            '@type': 'Question',
            name: 'Do you store my invoice data?',
            acceptedAnswer: { '@type': 'Answer', text: 'No, invoices are generated in your browser and you control your downloads.' }
          }
        ]
      };
      let script = document.getElementById('faq-schema');
      if (!script) {
        script = document.createElement('script');
        script.id = 'faq-schema';
        script.type = 'application/ld+json';
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify(faqSchema);

      // WebApplication structured data
      const appSchema = {
        '@context': 'https://schema.org',
        '@type': 'WebApplication',
        name: 'Modern Munshiji Invoice Generator',
        url: typeof window !== 'undefined' ? window.location.href : 'https://modernmunshiji.com/tools/free-online-invoice-generator',
        applicationCategory: 'BusinessApplication',
        operatingSystem: 'Web',
        description,
        offers: { '@type': 'Offer', price: '0', priceCurrency: 'INR' }
      };
      let appScript = document.getElementById('app-schema');
      if (!appScript) {
        appScript = document.createElement('script');
        appScript.id = 'app-schema';
        appScript.type = 'application/ld+json';
        document.head.appendChild(appScript);
      }
      appScript.textContent = JSON.stringify(appSchema);
    } catch (e) {
      // no-op if DOM not available
    }
  }, [isWeb]);

  const computeLineTotals = (line) => {
    const amount = (parseFloat(line.qty) || 0) * (parseFloat(line.rate) || 0);
    const gstRate = parseFloat(line.gstRate) || 0;
    let cgst = 0, sgst = 0, igst = 0;
    if (!charges.hideTaxes) {
      if (invoice.gstType === 'IGST') {
        igst = amount * (gstRate / 100);
      } else {
        const half = gstRate / 2;
        cgst = amount * (half / 100);
        sgst = amount * (half / 100);
      }
    }
    const total = amount + cgst + sgst + igst;
    return { amount, cgst, sgst, igst, total };
  };

  const summary = useMemo(() => {
    let subtotal = 0, cgst = 0, sgst = 0, igst = 0, qtyTotal = 0;
    items.forEach((it) => {
      const t = computeLineTotals(it);
      subtotal += t.amount;
      cgst += t.cgst;
      sgst += t.sgst;
      igst += t.igst;
      qtyTotal += parseFloat(it.qty) || 0;
    });
    const taxes = charges.hideTaxes ? 0 : (cgst + sgst + igst);
    let total = subtotal + taxes + (parseFloat(charges.addlCharges) || 0) - (parseFloat(charges.discount) || 0);
    if (charges.roundMode === 'round_up') total = Math.ceil(total);
    else if (charges.roundMode === 'round_down') total = Math.floor(total);
    return { subtotal, cgst, sgst, igst, taxes, total, qtyTotal };
  }, [items, charges, invoice.gstType]);

  // Defer heavy preview updates so typing in inputs doesn't cause immediate re-render
  const dInvoice = useDeferredValue(invoice);
  const dSeller = useDeferredValue(seller);
  const dClient = useDeferredValue(client);
  const dShipping = useDeferredValue(shipping);
  const dItems = useDeferredValue(items);
  const dCharges = useDeferredValue(charges);
  const dNotes = useDeferredValue(notes);
  const dTerms = useDeferredValue(terms);
  const dContactDetails = useDeferredValue(contactDetails);
  const dPayment = useDeferredValue(payment);
  const dSummary = useDeferredValue(summary);

  const numberToWords = (num) => {
    // Minimal Indian number words for totals (supports up to crores)
    const a = ['', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten', 'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen', 'seventeen', 'eighteen', 'nineteen'];
    const b = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'];
    const inWords = (n) => {
      if (n < 20) return a[n];
      if (n < 100) return b[Math.floor(n / 10)] + (n % 10 ? ' ' + a[n % 10] : '');
      if (n < 1000) return a[Math.floor(n / 100)] + ' hundred' + (n % 100 ? ' ' + inWords(n % 100) : '');
      if (n < 100000) return inWords(Math.floor(n / 1000)) + ' thousand' + (n % 1000 ? ' ' + inWords(n % 1000) : '');
      if (n < 10000000) return inWords(Math.floor(n / 100000)) + ' lakh' + (n % 100000 ? ' ' + inWords(n % 100000) : '');
      return inWords(Math.floor(n / 10000000)) + ' crore' + (n % 10000000 ? ' ' + inWords(n % 10000000) : '');
    };
    const whole = Math.floor(num);
    const frac = Math.round((num - whole) * 100);
    const words = inWords(whole) + ' ' + (invoice.currency === 'INR' ? 'rupees' : 'units');
    return frac ? words + ' and ' + inWords(frac) + ' paise' : words;
  };

  const updateItem = (id, field, value) => {
    setItems(prev => prev.map(it => it.id === id ? { ...it, [field]: value } : it));
  };

  const addNewLine = () => {
    const nextId = (items[items.length - 1]?.id || 0) + 1;
    setItems(prev => [...prev, { id: nextId, name: '', hsn: '', gstRate: 18, qty: 1, rate: 1, description: '', showDescription: false, duplicateOf: null }]);
  };

  const buildPayload = useCallback(() => ({
    user_id: session?.user?.id || null,
    title: invoice.title,
    invoice_number: invoice.number,
    // Ensure empty strings are not sent to date columns
    invoice_date: invoice.invoiceDate || null,
    show_due_date: invoice.showDueDate,
    due_date: (invoice.showDueDate && invoice.dueDate) ? invoice.dueDate : null,
    currency: invoice.currency,
    gst_type: invoice.gstType,
    seller,
    client,
    shipping,
    items,
    charges,
    payment,
    notes,
    terms,
    contact_details: contactDetails,
    attachments,
    logo_uri: logoUri,
    subtotal: summary.subtotal,
    tax_total: summary.taxes,
    total: summary.total,
    cgst: summary.cgst,
    sgst: summary.sgst,
    igst: summary.igst,
    discount_total: parseFloat(charges.discount) || 0,
    additional_charges: parseFloat(charges.addlCharges) || 0,
    created_at: new Date().toISOString(),
  }), [session?.user?.id, invoice, seller, client, shipping, items, charges, payment, notes, terms, contactDetails, attachments, logoUri, summary]);

  const performSave = useCallback(async (payloadOverride) => {
    if (!session?.user) return;
    setSaving(true);
    setSaveMessage('');
    const payload = payloadOverride || buildPayload();
    const isUpdating = !!editingInvoiceId;

    try {
      // Helpful diagnostics in console to trace issues
      console.log('[Invoice Save] supabase url:', SUPABASE_URL);
      console.log('[Invoice Save] session user id:', session?.user?.id);
      console.log('[Invoice Save] payload user_id:', payload?.user_id);
      console.log('[Invoice Save] isUpdating:', isUpdating, 'editingInvoiceId:', editingInvoiceId);
      console.log('[Invoice Save] invoice_date:', payload?.invoice_date, 'due_date:', payload?.due_date);

      // Quick connectivity ping to Supabase (auth settings endpoint responds fast if reachable)
      const pingUrl = `${SUPABASE_URL}/auth/v1/settings`;
      const t0 = Date.now();
      try {
        const pingResp = await fetch(pingUrl, { headers: { apikey: SUPABASE_ANON_KEY } });
        console.log('[Supabase Ping] status:', pingResp.status, 'latency(ms):', Date.now() - t0);
        if (!pingResp.ok) {
          console.warn('[Supabase Ping] Non-OK response; continuing save.');
        }
      } catch (netErr) {
        console.error('[Supabase Ping] Failed:', netErr?.name || 'Error', netErr?.message || netErr);
        setSaveMessage('Save failed: [ENETUNREACH] Supabase is not reachable. Disable VPN/ad-blocker, try Incognito, or check firewall settings.');
        setSaving(false);
        return;
      }

      const token = session?.access_token || session?.accessToken || null;
      let data = null;
      let error = null;

      if (isUpdating) {
        // UPDATE existing invoice via REST API
        console.log('[Invoice Save] Updating existing invoice:', editingInvoiceId);
        const updateUrl = `${SUPABASE_URL}/rest/v1/${INVOICE_TABLE}?id=eq.${editingInvoiceId}&user_id=eq.${session.user.id}`;
        const updatePayload = { ...payload };
        delete updatePayload.created_at; // Don't update created_at
        delete updatePayload.user_id; // Don't update user_id on update

        try {
          const resp = await fetch(updateUrl, {
            method: 'PATCH',
            headers: {
              apikey: SUPABASE_ANON_KEY,
              Authorization: token ? `Bearer ${token}` : `Bearer ${SUPABASE_ANON_KEY}`,
              'Content-Type': 'application/json',
              Prefer: 'return=representation',
              Accept: 'application/json',
            },
            body: JSON.stringify(updatePayload),
          });
          const text = await resp.text();
          let json = null;
          try { json = JSON.parse(text); } catch (_) { }
          console.log('[Invoice Save] update status:', resp.status);

          if (!resp.ok) {
            const errMsg = (json && json.message) ? json.message : (text || `${resp.status} ${resp.statusText}`);
            error = { message: errMsg, code: String(resp.status) };
          } else {
            const arr = Array.isArray(json) ? json : [json];
            data = arr[0] || null;
            console.log('[Invoice Save] updated row:', data);
          }
        } catch (fetchErr) {
          console.error('[Invoice Save] update network error:', fetchErr);
          error = { message: `PATCH failed: ${fetchErr?.message || fetchErr}` };
        }
      } else {
        // INSERT new invoice
        const insertPromise = supabase
          .from(INVOICE_TABLE)
          .insert(payload)
          .select('id, created_at')
          .single();

        const timeoutPromise = new Promise((resolve) => {
          const ms = 15000; // 15s network timeout safeguard
          setTimeout(() => resolve({ data: null, error: { message: 'Timed out contacting Supabase after 15s.', code: 'ETIMEOUT' } }), ms);
        });

        const result = await Promise.race([insertPromise, timeoutPromise]);
        data = result.data;
        error = result.error;

        if (error && error.code === 'ETIMEOUT') {
          console.warn('[Invoice Save] Falling back to direct fetch POST…');
          const url = `${SUPABASE_URL}/rest/v1/${INVOICE_TABLE}?select=id,created_at`;
          try {
            const resp = await fetch(url, {
              method: 'POST',
              headers: {
                apikey: SUPABASE_ANON_KEY,
                Authorization: token ? `Bearer ${token}` : `Bearer ${SUPABASE_ANON_KEY}`,
                'Content-Type': 'application/json',
                Prefer: 'return=representation',
                Accept: 'application/json',
              },
              body: JSON.stringify(payload),
            });
            const text = await resp.text();
            let json = null;
            try { json = JSON.parse(text); } catch (_) { }
            console.log('[Invoice Save] fetch fallback status:', resp.status);
            if (!resp.ok) {
              const errMsg = (json && json.message) ? json.message : (text || `${resp.status} ${resp.statusText}`);
              error = { message: errMsg, code: String(resp.status) };
            } else {
              const arr = Array.isArray(json) ? json : [json];
              data = arr[0] || null;
              error = null;
              console.log('[Invoice Save] fetch fallback inserted row:', data);
            }
          } catch (fetchErr) {
            console.error('[Invoice Save] fetch fallback network error:', fetchErr);
            error = { message: `FETCH_FAIL: ${fetchErr?.message || fetchErr}` };
          }
        }
      }

      if (error) {
        console.error('Supabase save error:', error);
        const code = error?.code ? ` [${error.code}]` : '';
        const hint = error?.hint ? ` Hint: ${error.hint}` : '';
        setSaveMessage(`Save failed:${code} ${error.message || 'Unknown error.'}${hint}`);
      } else {
        console.log('[Invoice Save] saved row:', data);
        setSaveMessage(isUpdating ? 'Invoice updated successfully.' : 'Invoice saved successfully.');
        // Reset editing state after successful save
        setEditingInvoiceId(null);
        // Redirect to dashboard after successful save
        try { navigation.navigate('Dashboard'); } catch (_) { }
      }
    } catch (e) {
      console.error('Unexpected save error:', e);
      setSaveMessage(`Save failed: ${e.message || e}`);
    }
    setSaving(false);
  }, [session?.user, buildPayload, navigation, editingInvoiceId]);

  useEffect(() => {
    if (session?.user) {
      // Resume any previously queued save payload (e.g., after email confirmation and login)
      const raw = isWeb ? window.localStorage.getItem('pendingInvoiceSave') : null;
      if (raw) {
        try {
          const queued = JSON.parse(raw);
          // inject user_id from fresh session
          queued.user_id = session.user.id;
          performSave(queued);
        } catch (_) { }
        if (isWeb) window.localStorage.removeItem('pendingInvoiceSave');
        setPendingSave(false);
      } else if (pendingSave) {
        performSave();
        setPendingSave(false);
      }
    }
  }, [pendingSave, session?.user, performSave]);

  const handleSaveInvoice = () => {
    // Block save if there are validation errors or missing required fields
    const hasInvoiceErrors = Object.keys(errors.invoice || {}).length > 0;
    const hasSellerErrors = Object.keys(errors.seller || {}).length > 0;
    const hasClientErrors = Object.keys(errors.client || {}).length > 0;
    const hasShippingErrors = Object.keys(errors.shipping || {}).length > 0;
    const hasPaymentErrors = Object.keys(errors.payment || {}).length > 0;
    const hasItemErrors = Object.values(errors.items || {}).some((e) => e && Object.keys(e).length > 0);

    const missingRequired = [];
    if (!invoice.invoiceDate) missingRequired.push('Invoice Date');
    if (invoice.showDueDate && !invoice.dueDate) missingRequired.push('Due Date');
    if (!seller.businessName) missingRequired.push('Billed By: Business Name');
    if (!client.businessName) missingRequired.push('Billed To: Business Name');
    if (!Array.isArray(items) || items.length === 0) missingRequired.push('At least one Item');

    const hasAnyIssues = hasInvoiceErrors || hasSellerErrors || hasClientErrors || hasShippingErrors || hasPaymentErrors || hasItemErrors || missingRequired.length > 0;
    if (hasAnyIssues) {
      const parts = [];
      if (missingRequired.length) parts.push(`Required: ${missingRequired.join(', ')}`);
      if (hasInvoiceErrors || hasSellerErrors || hasClientErrors || hasShippingErrors || hasPaymentErrors || hasItemErrors) parts.push('Fix highlighted field errors');
      const msg = `Cannot save yet. ${parts.join(' • ')}.`;
      setSaveMessage(msg);
      try { Alert.alert('Incomplete Invoice', 'Please fill the required fields highlighted in red before saving.'); } catch (_) { }
      return;
    }

    if (!session?.user) {
      // Queue the save payload so it can be automatically saved after login
      const queued = buildPayload();
      if (isWeb) window.localStorage.setItem('pendingInvoiceSave', JSON.stringify(queued));
      setPendingSave(true);
      openAuthModal();
      setSaveMessage('Please login/sign up to save. We will resume automatically after you sign in.');
      return;
    }
    performSave();
  };

  const duplicateLine = (id) => {
    const src = items.find(it => it.id === id);
    if (!src) return;
    const nextId = (items[items.length - 1]?.id || 0) + 1;
    setItems(prev => [...prev, { ...src, id: nextId, duplicateOf: id }]);
  };

  const removeLine = (id) => {
    setItems(prev => prev.filter(it => it.id !== id));
  };

  const handleLogoPick = useCallback((e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setLogoUri(reader.result);
    reader.readAsDataURL(file);
  }, [setLogoUri]);

  const LogoPicker = React.memo(({ currentLogoUri }) => {
    if (!isWeb) return null;
    const inputRef = useRef(null);
    return (
      <View style={{ alignItems: 'flex-end' }}>
        <TouchableOpacity style={styles.outlineButton} onPress={() => inputRef.current?.click?.()}>
          <Ionicons name="image-outline" size={16} color="#4F46E5" />
          <Text style={styles.outlineButtonText}>Add Business Logo</Text>
        </TouchableOpacity>
        {React.createElement('input', { type: 'file', accept: 'image/*', style: { display: 'none' }, ref: inputRef, onChange: handleLogoPick })}
        {currentLogoUri ? <Image source={{ uri: currentLogoUri }} style={{ width: 80, height: 80, marginTop: 8, borderRadius: 8 }} /> : null}
      </View>
    );
  });

  const exportPDF = async () => {
    try {
      if (!isWeb) return; // web-only export
      const modCanvas = await import('html2canvas');
      const modPdf = await import('jspdf');
      const html2canvas = modCanvas.default;
      const { jsPDF } = modPdf;

      const target = document.getElementById('invoice-print');
      if (!target) return;
      const canvas = await html2canvas(target, { scale: 2, useCORS: true });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'pt', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = pageWidth - 40; // margin
      const imgHeight = canvas.height * imgWidth / canvas.width;
      let y = 20;
      if (imgHeight <= pageHeight - 40) {
        pdf.addImage(imgData, 'PNG', 20, y, imgWidth, imgHeight);
      } else {
        // Simple multi-page: slice vertically by shifting
        let heightLeft = imgHeight;
        let position = 20;
        while (heightLeft > 0) {
          pdf.addImage(imgData, 'PNG', 20, position, imgWidth, imgHeight);
          heightLeft -= pageHeight;
          if (heightLeft > 0) {
            pdf.addPage();
            position = 20 - (imgHeight - heightLeft);
          }
        }
      }
      pdf.save(`Invoice_${invoice.number}.pdf`);
    } catch (err) {
      console.error('PDF export failed', err);
    }
  };

  // ----- Saved Invoices helpers -----
  const getMonthKey = (dIso, fallbackIso) => {
    const iso = dIso || fallbackIso || '';
    if (!iso || typeof iso !== 'string') return 'unknown';
    const parts = iso.split('T')[0].split('-');
    const y = parts[0];
    const m = parts[1];
    if (!y || !m) return 'unknown';
    return `${y}-${m}`; // YYYY-MM
  };

  const refreshSavedInvoices = async () => {
    if (!session?.user) return;
    setSavedLoading(true);
    setSavedError('');
    try {
      const { data, error } = await supabase
        .from(INVOICE_TABLE)
        .select('id, title, invoice_number, invoice_date, currency, total, client, created_at, paid, billing_month')
        .eq('user_id', session.user.id)
        .order('created_at', { ascending: false });
      if (error) {
        setSavedError(error.message);
        setSavedInvoices([]);
      } else {
        const rows = (data || []).map((r) => ({
          ...r,
          // Normalize fields for robustness
          invoice_date: r.invoice_date || null,
          billing_month: r.billing_month || getMonthKey(r.invoice_date, r.created_at),
          paid: !!r.paid,
        }));
        setSavedInvoices(rows);
        computeMonthStats(rows, selectedMonth);
      }
    } catch (e) {
      setSavedError(e?.message || String(e));
    }
    setSavedLoading(false);
  };

  const computeMonthStats = (rows, month) => {
    const filtered = month === 'all' ? rows : rows.filter((r) => (r.billing_month || getMonthKey(r.invoice_date, r.created_at)) === month);
    const totalCount = filtered.length;
    const paidCount = filtered.filter((r) => !!r.paid).length;
    const dueCount = totalCount - paidCount;
    const totalAmount = filtered.reduce((sum, r) => sum + (parseFloat(r.total) || 0), 0);
    setMonthStats({ totalCount, paidCount, dueCount, totalAmount });
  };

  useEffect(() => {
    if (session?.user) refreshSavedInvoices();
  }, [session?.user]);

  useEffect(() => {
    // Recompute stats when filter changes
    computeMonthStats(savedInvoices, selectedMonth);
  }, [selectedMonth, savedInvoices]);

  const loadInvoiceFromRow = (row) => {
    // Safely parse JSON-like fields that may be stored as strings
    const parseMaybeJson = (val, fallback) => {
      if (val == null) return fallback;
      if (typeof val === 'string') {
        try { return JSON.parse(val); } catch (_) { return fallback; }
      }
      return val;
    };

    // Set the editing ID so Save becomes Update
    if (row.id) {
      setEditingInvoiceId(row.id);
    }

    const nextSeller = parseMaybeJson(row.seller, seller);
    const nextClient = parseMaybeJson(row.client, client);
    const nextShipping = parseMaybeJson(row.shipping, shipping);
    const nextItems = parseMaybeJson(row.items, items);
    const nextCharges = parseMaybeJson(row.charges, charges);
    const nextPayment = parseMaybeJson(row.payment, payment);
    const nextAttachments = parseMaybeJson(row.attachments, []);

    setInvoice((p) => ({
      ...p,
      title: row.title || p.title,
      number: row.invoice_number || p.number,
      invoiceDate: row.invoice_date || p.invoiceDate,
      showDueDate: typeof row.show_due_date === 'boolean' ? row.show_due_date : p.showDueDate,
      dueDate: row.due_date || p.dueDate,
      currency: row.currency || p.currency,
      gstType: row.gst_type || p.gstType,
    }));
    setSeller(nextSeller || seller);
    setClient(nextClient || client);
    setShipping(nextShipping || shipping);
    setItems(Array.isArray(nextItems) ? nextItems : items);
    setCharges(nextCharges || charges);
    setPayment(nextPayment || payment);
    setNotes(typeof row.notes === 'string' ? row.notes : (row.notes ?? ''));
    setTerms(typeof row.terms === 'string' ? row.terms : (row.terms ?? ''));
    setContactDetails(typeof row.contact_details === 'string' ? row.contact_details : (row.contact_details ?? ''));
    setAttachments(Array.isArray(nextAttachments) ? nextAttachments : []);
    setLogoUri(typeof row.logo_uri === 'string' ? row.logo_uri : (row.logo_uri ?? ''));

    // If shipping has any content, ensure the section toggles on
    const hasShipping = !!(nextShipping?.address || nextShipping?.city || nextShipping?.postalCode);
    if (hasShipping) setShowShipping(true);
  };

  const confirmDelete = () => {
    if (Platform.OS === 'web') {
      try { return Promise.resolve(window.confirm('Delete this invoice permanently?')); } catch (_) { return Promise.resolve(true); }
    }
    return new Promise((resolve) => {
      Alert.alert('Delete invoice', 'Are you sure you want to delete this invoice?', [
        { text: 'Cancel', style: 'cancel', onPress: () => resolve(false) },
        { text: 'Delete', style: 'destructive', onPress: () => resolve(true) },
      ]);
    });
  };

  const deleteInvoiceRow = async (row) => {
    const ok = await confirmDelete();
    if (!ok) return;
    try {
      // Try SDK delete first with short timeout
      const sdkDelete = supabase.from(INVOICE_TABLE).delete().eq('id', row.id).eq('user_id', session.user.id);
      const timeout = new Promise((resolve) => setTimeout(() => resolve({ error: { message: 'Timed out', code: 'ETIMEOUT' } }), 6000));
      let result = await Promise.race([sdkDelete, timeout]);
      if (result && result.error) {
        const token = session?.access_token || session?.accessToken || null;
        const url = `${SUPABASE_URL}/rest/v1/${INVOICE_TABLE}?id=eq.${row.id}&user_id=eq.${session.user.id}`;
        const headers = {
          apikey: SUPABASE_ANON_KEY,
          Accept: 'application/json',
          Authorization: `Bearer ${token || SUPABASE_ANON_KEY}`,
          Prefer: 'return=minimal',
        };
        const resp = await fetch(url, { method: 'DELETE', headers });
        if (!resp.ok) throw new Error(`Delete failed (${resp.status})`);
      }
      await refreshSavedInvoices();
    } catch (e) {
      setSavedError(e?.message || String(e));
    }
  };

  const togglePaidRow = async (row) => {
    try {
      const nextPaid = !row.paid;
      const sdkUpdate = supabase
        .from(INVOICE_TABLE)
        .update({ paid: nextPaid })
        .eq('id', row.id)
        .eq('user_id', session.user.id)
        .select('id, paid');
      const timeout = new Promise((resolve) => setTimeout(() => resolve({ error: { message: 'Timed out', code: 'ETIMEOUT' } }), 6000));
      let result = await Promise.race([sdkUpdate, timeout]);
      if ((result && result.error) || !result?.data || (Array.isArray(result.data) && result.data.length === 0)) {
        const token = session?.access_token || session?.accessToken || null;
        const url = `${SUPABASE_URL}/rest/v1/${INVOICE_TABLE}?id=eq.${row.id}&user_id=eq.${session.user.id}`;
        const headers = {
          apikey: SUPABASE_ANON_KEY,
          Accept: 'application/json',
          Authorization: `Bearer ${token || SUPABASE_ANON_KEY}`,
          Prefer: 'return=representation',
          'Content-Type': 'application/json',
        };
        const resp = await fetch(url, { method: 'PATCH', headers, body: JSON.stringify({ paid: nextPaid }) });
        if (!resp.ok) throw new Error(`Paid toggle failed (${resp.status})`);
      }
      await refreshSavedInvoices();
    } catch (e) {
      setSavedError(e?.message || String(e));
    }
  };

  const changeRowMonth = async (row, newMonth) => {
    try {
      const sdkUpdate = supabase
        .from(INVOICE_TABLE)
        .update({ billing_month: newMonth })
        .eq('id', row.id)
        .eq('user_id', session.user.id);
      const timeout = new Promise((resolve) => setTimeout(() => resolve({ error: { message: 'Timed out', code: 'ETIMEOUT' } }), 6000));
      let result = await Promise.race([sdkUpdate, timeout]);
      if (result && result.error) {
        const token = session?.access_token || session?.accessToken || null;
        const url = `${SUPABASE_URL}/rest/v1/${INVOICE_TABLE}?id=eq.${row.id}&user_id=eq.${session.user.id}`;
        const headers = {
          apikey: SUPABASE_ANON_KEY,
          Accept: 'application/json',
          Authorization: `Bearer ${token || SUPABASE_ANON_KEY}`,
          Prefer: 'return=minimal',
          'Content-Type': 'application/json',
        };
        const resp = await fetch(url, { method: 'PATCH', headers, body: JSON.stringify({ billing_month: newMonth }) });
        if (!resp.ok) throw new Error(`Month update failed (${resp.status})`);
      }
      await refreshSavedInvoices();
    } catch (e) {
      setSavedError(e?.message || String(e));
    }
  };

  const exportInvoiceRow = async (row) => {
    loadInvoiceFromRow(row);
    // Give React a tick to apply state before export
    await new Promise((r) => setTimeout(r, 100));
    await exportPDF();
  };

  const sendInvoiceToClient = (row) => {
    const email = row?.client?.email || '';
    const subject = encodeURIComponent(`Invoice ${row?.invoice_number || ''}`);
    const body = encodeURIComponent(
      `Dear ${row?.client?.businessName || 'Client'},\n\nPlease find your invoice details below:\nInvoice No: ${row?.invoice_number || ''}\nInvoice Date: ${row?.invoice_date || ''}\nTotal: ${row?.total || ''} ${row?.currency || ''}\n\nBest regards,\n${seller.businessName || 'Modern Munshiji'}`
    );
    if (isWeb) {
      const mailto = `mailto:${email}?subject=${subject}&body=${body}`;
      try { window.open(mailto, '_blank'); } catch (_) { }
    }
  };

  // Duplicate invoice as Draft with "Copy" suffix
  const duplicateInvoiceRow = async (row) => {
    try {
      const token = session?.access_token || session?.accessToken || null;

      // Load full invoice data via REST API
      let fullRow = null;
      const loadUrl = `${SUPABASE_URL}/rest/v1/${INVOICE_TABLE}?id=eq.${row.id}&user_id=eq.${session.user.id}&select=*`;
      const loadHeaders = {
        apikey: SUPABASE_ANON_KEY,
        Accept: 'application/json',
        Authorization: `Bearer ${token || SUPABASE_ANON_KEY}`,
      };
      const loadResp = await fetch(loadUrl, { headers: loadHeaders });
      const loadText = await loadResp.text();
      try {
        const parsed = JSON.parse(loadText);
        if (Array.isArray(parsed) && parsed.length > 0) fullRow = parsed[0];
      } catch (_) { }

      if (!fullRow) {
        setSavedError('Failed to load invoice for duplication');
        return;
      }

      const todayIso = new Date().toISOString().split('T')[0];
      const origNumber = fullRow.invoice_number || '';
      const newNumber = origNumber.includes('Copy') ? origNumber : `${origNumber}-Copy`;

      // Create new invoice payload with all fields
      const newInvoice = {
        user_id: session.user.id,
        title: fullRow.title || 'Invoice',
        invoice_number: newNumber,
        invoice_date: todayIso,
        due_date: null,
        show_due_date: false,
        currency: fullRow.currency || 'INR',
        gst_type: fullRow.gst_type || 'IGST',
        seller: fullRow.seller || null,
        client: fullRow.client || null,
        shipping: fullRow.shipping || null,
        items: fullRow.items || null,
        charges: fullRow.charges || null,
        payment: fullRow.payment || null,
        notes: fullRow.notes || null,
        terms: fullRow.terms || null,
        contact_details: fullRow.contact_details || null,
        logo_uri: fullRow.logo_uri || null,
        attachments: fullRow.attachments || null,
        total: fullRow.total || 0,
        paid: false,
        billing_month: getMonthKey(todayIso, todayIso),
      };

      // Insert via REST API
      const insertUrl = `${SUPABASE_URL}/rest/v1/${INVOICE_TABLE}`;
      const insertHeaders = {
        apikey: SUPABASE_ANON_KEY,
        Accept: 'application/json',
        Authorization: `Bearer ${token || SUPABASE_ANON_KEY}`,
        'Content-Type': 'application/json',
        Prefer: 'return=representation',
      };
      const insertResp = await fetch(insertUrl, { method: 'POST', headers: insertHeaders, body: JSON.stringify(newInvoice) });

      if (!insertResp.ok) {
        setSavedError(`Duplicate failed (${insertResp.status})`);
        return;
      }

      await refreshSavedInvoices();
      if (isWeb) {
        try { window.alert(`Invoice duplicated as "${newNumber}" (Draft)`); } catch (_) { }
      }
    } catch (e) {
      setSavedError(e?.message || 'Duplicate failed');
    }
  };

  const renderSavedInvoices = () => {
    const monthKeys = Array.from(new Set(savedInvoices.map((r) => r.billing_month || getMonthKey(r.invoice_date, r.created_at))));
    const filtered = selectedMonth === 'all'
      ? savedInvoices
      : savedInvoices.filter((r) => (r.billing_month || getMonthKey(r.invoice_date, r.created_at)) === selectedMonth);
    return (
      <View style={{ gap: 12 }}>
        {/* Filters and stats */}
        <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
          {isWeb && (
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
              <Text style={styles.helperText}>Month</Text>
              {React.createElement('select', {
                value: selectedMonth,
                onChange: (e) => setSelectedMonth(e.target.value),
                style: { padding: 6, borderRadius: 6, border: '1px solid #e5e7eb', backgroundColor: '#f8fafc' },
              }, [
                React.createElement('option', { key: 'all', value: 'all' }, 'All'),
                ...monthKeys.map((m) => React.createElement('option', { key: m, value: m }, m)),
              ])}
            </View>
          )}
          <View style={{ flexDirection: 'row', gap: 8, flexWrap: 'wrap' }}>
            <View style={styles.statCard}><Text style={styles.statTitle}>Invoices</Text><Text style={styles.statValue}>{monthStats.totalCount}</Text></View>
            <View style={styles.statCard}><Text style={styles.statTitle}>Paid</Text><Text style={styles.statValue}>{monthStats.paidCount}</Text></View>
            <View style={styles.statCard}><Text style={styles.statTitle}>Due</Text><Text style={styles.statValue}>{monthStats.dueCount}</Text></View>
            <View style={styles.statCard}><Text style={styles.statTitle}>Total</Text><Text style={styles.statValue}>{formatCurrency(monthStats.totalAmount, currencySymbol)}</Text></View>
          </View>
        </View>

        {/* Table header */}
        <View style={styles.tableHeaderRow}>
          <Text style={[styles.tableHeaderCell, { flex: 1 }]}>#</Text>
          <Text style={[styles.tableHeaderCell, { flex: 2 }]}>Invoice No</Text>
          <Text style={[styles.tableHeaderCell, { flex: 2 }]}>Date</Text>
          <Text style={[styles.tableHeaderCell, { flex: 3 }]}>Client</Text>
          <Text style={[styles.tableHeaderCell, { flex: 2 }]}>Total</Text>
          <Text style={[styles.tableHeaderCell, { flex: 2 }]}>Status</Text>
          <Text style={[styles.tableHeaderCell, { flex: 2 }]}>Month</Text>
          <Text style={[styles.tableHeaderCell, { flex: 3 }]}>Actions</Text>
        </View>

        {/* Table rows */}
        {savedLoading && (<Text style={styles.helperText}>Loading invoices…</Text>)}
        {!!savedError && (<Text style={{ color: 'crimson' }}>{savedError}</Text>)}
        {!savedLoading && filtered.length === 0 && (<Text style={styles.helperText}>No invoices found for the selected month.</Text>)}
        {filtered.map((row, idx) => (
          <View key={row.id} style={styles.tableRow}>
            <Text style={[styles.tableCell, { flex: 1 }]}>{idx + 1}</Text>
            <Text style={[styles.tableCell, { flex: 2 }]}>{row.invoice_number || '—'}</Text>
            <Text style={[styles.tableCell, { flex: 2 }]}>{row.invoice_date || '—'}</Text>
            <Text style={[styles.tableCell, { flex: 3 }]}>{row?.client?.businessName || '—'}</Text>
            <Text style={[styles.tableCell, { flex: 2 }]}>{formatCurrency(parseFloat(row.total) || 0, currencySymbol)} {row.currency || ''}</Text>
            <View style={[styles.tableCell, { flex: 2 }]}>
              <Text style={[styles.pill, { backgroundColor: row.paid ? '#DCFCE7' : '#FEE2E2', color: row.paid ? '#065f46' : '#7F1D1D' }]}>{row.paid ? 'Paid' : 'Due'}</Text>
            </View>
            <View style={[styles.tableCell, { flex: 2 }]}>
              {isWeb ? React.createElement('select', {
                value: row.billing_month || getMonthKey(row.invoice_date, row.created_at),
                onChange: (e) => changeRowMonth(row, e.target.value),
                style: { padding: 6, borderRadius: 6, border: '1px solid #e5e7eb', backgroundColor: '#f8fafc' },
              }, [
                ...monthKeys.map((m) => React.createElement('option', { key: m, value: m }, m)),
              ]) : (
                <Text style={styles.helperText}>{row.billing_month || getMonthKey(row.invoice_date, row.created_at)}</Text>
              )}
            </View>
            <View style={[styles.tableCell, { flex: 3, flexDirection: 'row', gap: 8, flexWrap: 'wrap' }]}>
              <TouchableOpacity style={styles.iconButton} onPress={() => { loadInvoiceFromRow(row); }}>
                <Ionicons name="eye-outline" size={16} color="#334155" />
              </TouchableOpacity>
              <TouchableOpacity style={styles.iconButton} onPress={() => duplicateInvoiceRow(row)}>
                <Ionicons name="copy-outline" size={16} color="#334155" />
              </TouchableOpacity>
              <TouchableOpacity style={styles.iconButton} onPress={() => exportInvoiceRow(row)}>
                <Ionicons name="download-outline" size={16} color="#334155" />
              </TouchableOpacity>
              <TouchableOpacity style={styles.iconButton} onPress={() => sendInvoiceToClient(row)}>
                <Ionicons name="mail-outline" size={16} color="#334155" />
              </TouchableOpacity>
              <TouchableOpacity style={styles.iconButton} onPress={() => togglePaidRow(row)}>
                <Ionicons name={row.paid ? 'checkmark-done-outline' : 'alert-circle-outline'} size={16} color={row.paid ? '#065f46' : '#7F1D1D'} />
              </TouchableOpacity>
              <TouchableOpacity style={styles.iconButton} onPress={() => deleteInvoiceRow(row)}>
                <Ionicons name="trash-outline" size={16} color="#7F1D1D" />
              </TouchableOpacity>
            </View>
          </View>
        ))}
      </View>
    );
  };

  const renderPrintInvoice = () => {
    const isClassic = template === 'classic';
    const isEmerald = template === 'emerald';
    const isMinimal = template === 'minimal';
    const isSlate = template === 'slate';

    const themes = {
      classic: {
        accent: '#2563EB',
        accentSoft: '#DBEAFE',
        headerTitleColor: '#111827',
        brandTextColor: '#0f172a',
        blockBorder: '#e5e7eb',
        blockBg: '#ffffff',
        tableHeaderBg: '#F3F4F6',
        tableHeaderText: '#374151',
        grandColor: '#065f46',
      },
      emerald: {
        accent: '#6D28D9',
        accentSoft: '#EDE9FE',
        headerTitleColor: '#6D28D9',
        brandTextColor: '#0f172a',
        blockBorder: '#6D28D9',
        blockBg: '#F5F3FF',
        tableHeaderBg: '#6D28D9',
        tableHeaderText: '#ffffff',
        grandColor: '#6D28D9',
      },
      minimal: {
        accent: '#111827',
        accentSoft: '#ffffff',
        headerTitleColor: '#111827',
        brandTextColor: '#111827',
        blockBorder: '#e5e7eb',
        blockBg: '#ffffff',
        tableHeaderBg: '#ffffff',
        tableHeaderText: '#111827',
        grandColor: '#111827',
      },
      slate: {
        accent: '#0F172A',
        accentSoft: '#1F2937',
        headerTitleColor: '#0F172A',
        brandTextColor: '#0f172a',
        blockBorder: '#1F2937',
        blockBg: '#ffffff',
        tableHeaderBg: '#F3F4F6',
        tableHeaderText: '#374151',
        grandColor: '#0F172A',
      },
    };
    const theme = themes[template] || themes.classic;

    const printStyles = {
      page: { width: 794, minHeight: 1123, backgroundColor: '#ffffff', padding: 24, borderWidth: 1, borderColor: '#e5e7eb', position: 'relative' },
      headerRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 },
      brand: { flexDirection: 'row', alignItems: 'center', gap: 8 },
      brandText: { fontSize: 18, fontWeight: '700', color: theme.brandTextColor },
      invMetaRight: { alignItems: 'flex-end' },
      invTitle: { fontSize: 22, fontWeight: '800', color: theme.headerTitleColor },
      invNo: { fontSize: 14, color: '#374151', marginTop: 4 },
      block: { borderWidth: 1, borderColor: theme.blockBorder, backgroundColor: theme.blockBg, borderRadius: 8, padding: 12, marginTop: 12 },
      blockTitle: { fontSize: 14, fontWeight: '700', color: theme.headerTitleColor, marginBottom: 8 },
      twoCol: { flexDirection: 'row', gap: 12 },
      col: { flex: 1 },
      label: { fontSize: 11, color: '#6b7280' },
      value: { fontSize: 12, color: '#0f172a', fontWeight: '600', marginBottom: 4 },
      tableHeader: { flexDirection: 'row', backgroundColor: theme.tableHeaderBg, borderBottomWidth: 1, borderBottomColor: '#e5e7eb' },
      th: { paddingVertical: 8, paddingHorizontal: 6, fontSize: 11, fontWeight: '700', color: theme.tableHeaderText },
      row: { flexDirection: 'row', borderBottomWidth: 1, borderBottomColor: '#e5e7eb', alignItems: 'flex-start' },
      tdBox: { paddingVertical: 8, paddingHorizontal: 6 },
      td: { fontSize: 12, color: '#111827' },
      totals: { marginTop: 12, gap: 6 },
      totalsRow: { flexDirection: 'row', justifyContent: 'space-between' },
      totalsLabel: { fontSize: 12, color: '#374151' },
      totalsValue: { fontSize: 12, fontWeight: '700', color: '#0f172a' },
      grandRow: { flexDirection: 'row', justifyContent: 'space-between', marginTop: 8 },
      grandLabel: { fontSize: 14, fontWeight: '800', color: theme.headerTitleColor },
      grandValue: { fontSize: 16, fontWeight: '800', color: theme.grandColor },
      notes: { marginTop: 12 },
      small: { fontSize: 11, color: '#6b7280' },
      signatureRow: { flexDirection: 'row', justifyContent: 'space-between', marginTop: 24 },
      sigBlock: { width: 220, alignItems: 'center' },
      sigLine: { height: 1, backgroundColor: '#e5e7eb', alignSelf: 'stretch', marginTop: 24 },
      footerText: { position: 'absolute', bottom: 8, right: 12, fontSize: 9, color: '#9CA3AF' },
    };

    const lineFlexes = [2, 1, 1, 1, 1, invoice.gstType === 'IGST' ? 1 : 2, 1];

    return (
      <View style={printStyles.page}>
        {/* Header variations per template */}
        {isSlate ? (
          <View style={{ backgroundColor: theme.accent, borderRadius: 10, padding: 14, marginBottom: 2, flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
            <View style={[printStyles.brand, { gap: 10 }]}>
              {logoUri ? (
                <Image source={{ uri: logoUri }} style={{ width: 48, height: 48, borderRadius: 8 }} />
              ) : (
                <View style={{ width: 48, height: 48, borderRadius: 8, backgroundColor: theme.accentSoft, borderWidth: 1, borderColor: '#FFFFFF22' }} />
              )}
              <Text style={[printStyles.brandText, { color: '#ffffff' }]}>{seller.businessName || 'Your Business'}</Text>
            </View>
            <View style={{ alignItems: 'flex-end' }}>
              <Text style={[printStyles.invTitle, { color: '#ffffff' }]}>{invoice.title || 'Invoice'}</Text>
              <Text style={{ fontSize: 12, color: '#CBD5E1', marginTop: 4 }}>No: {invoice.number || '-'}</Text>
              <Text style={{ fontSize: 12, color: '#CBD5E1' }}>Date: {invoice.invoiceDate || '-'}</Text>
              {invoice.showDueDate && !!invoice.dueDate && (
                <Text style={{ fontSize: 12, color: '#CBD5E1' }}>Due: {invoice.dueDate}</Text>
              )}
            </View>
          </View>
        ) : isMinimal ? (
          <View style={printStyles.headerRow}>
            <View style={{ flex: 1 }}>
              <Text style={[printStyles.invTitle]}>{invoice.title || 'Invoice'}</Text>
              <View style={{ height: 2, backgroundColor: theme.accent, borderRadius: 1, marginTop: 6, marginBottom: 2 }} />
              <Text style={printStyles.small}>No: {invoice.number || '-'}</Text>
              <Text style={printStyles.small}>Date: {invoice.invoiceDate || '-'}</Text>
              {invoice.showDueDate && !!invoice.dueDate && (
                <Text style={printStyles.small}>Due: {invoice.dueDate}</Text>
              )}
            </View>
            <View style={printStyles.brand}>
              {logoUri ? (
                <Image source={{ uri: logoUri }} style={{ width: 40, height: 40, borderRadius: 8 }} />
              ) : (
                <View style={{ width: 40, height: 40, borderRadius: 8, backgroundColor: '#ffffff', borderWidth: 1, borderColor: '#e5e7eb' }} />
              )}
              <Text style={printStyles.brandText}>{seller.businessName || 'Your Business'}</Text>
            </View>
          </View>
        ) : (
          <View style={printStyles.headerRow}>
            <View style={printStyles.brand}>
              {logoUri ? (
                <Image source={{ uri: logoUri }} style={{ width: 48, height: 48, borderRadius: 8 }} />
              ) : (
                <View style={{ width: 48, height: 48, borderRadius: 8, backgroundColor: theme.accentSoft, borderWidth: 1, borderColor: theme.accent }} />
              )}
              <Text style={printStyles.brandText}>{seller.businessName || 'Your Business'}</Text>
            </View>
            <View style={printStyles.invMetaRight}>
              <Text style={printStyles.invTitle}>{invoice.title || 'Invoice'}</Text>
              <Text style={printStyles.invNo}>No: {invoice.number || '-'}</Text>
              <Text style={printStyles.small}>Date: {invoice.invoiceDate || '-'}</Text>
              {invoice.showDueDate && !!invoice.dueDate && (
                <Text style={printStyles.small}>Due: {invoice.dueDate}</Text>
              )}
            </View>
          </View>
        )}

        <View style={[printStyles.block, printStyles.twoCol]}>
          <View style={printStyles.col}>
            <Text style={printStyles.blockTitle}>Billed By</Text>
            <Text style={printStyles.value}>{seller.businessName}</Text>
            <Text style={printStyles.small}>{seller.address}</Text>
            <Text style={printStyles.small}>{seller.city} {seller.postalCode}</Text>
            {!!seller.country && <Text style={printStyles.small}>{seller.country}</Text>}
            {!!seller.gstin && <Text style={printStyles.small}>GSTIN: {seller.gstin}</Text>}
            {!!seller.pan && <Text style={printStyles.small}>PAN: {seller.pan}</Text>}
            {!!seller.email && <Text style={printStyles.small}>Email: {seller.email}</Text>}
          </View>
          <View style={printStyles.col}>
            <Text style={printStyles.blockTitle}>Billed To</Text>
            <Text style={printStyles.value}>{client.businessName}</Text>
            <Text style={printStyles.small}>{client.address}</Text>
            <Text style={printStyles.small}>{client.city} {client.postalCode}</Text>
            {!!client.country && <Text style={printStyles.small}>{client.country}</Text>}
            {!!client.gstin && <Text style={printStyles.small}>GSTIN: {client.gstin}</Text>}
            {!!client.pan && <Text style={printStyles.small}>PAN: {client.pan}</Text>}
            {!!client.email && <Text style={printStyles.small}>Email: {client.email}</Text>}
          </View>
        </View>

        {(shipping.address || shipping.city || shipping.postalCode) && (
          <View style={printStyles.block}>
            <Text style={printStyles.blockTitle}>Shipping Details</Text>
            <Text style={printStyles.small}>{shipping.address}</Text>
            <Text style={printStyles.small}>{shipping.city} {shipping.postalCode}</Text>
          </View>
        )}

        {/* Items table */}
        <View style={[printStyles.block]}>
          {/** Determine if any item has HSN/SAC value; hide column entirely if none */}
          {(() => { return null; })()}
          {/** Header */}
          {(() => {
            const hasHSN = items.some((ln) => (ln.hsn || '').toString().trim().length > 0);
            return (
              <View style={printStyles.tableHeader}>
                <Text style={[printStyles.th, { flex: 2 }]}>Item</Text>
                {hasHSN && <Text style={[printStyles.th, { flex: 1 }]}>HSN/SAC</Text>}
                <Text style={[printStyles.th, { flex: 1 }]}>GST%</Text>
                <Text style={[printStyles.th, { flex: 1 }]}>Qty</Text>
                <Text style={[printStyles.th, { flex: 1 }]}>Rate</Text>
                {invoice.gstType === 'CGST_SGST' && !charges.hideTaxes ? (
                  <>
                    <Text style={[printStyles.th, { flex: 1 }]}>CGST</Text>
                    <Text style={[printStyles.th, { flex: 1 }]}>SGST</Text>
                  </>
                ) : (!charges.hideTaxes && (
                  <Text style={[printStyles.th, { flex: 1 }]}>IGST</Text>
                ))}
                <Text style={[printStyles.th, { flex: 1 }]}>Total</Text>
              </View>
            );
          })()}
          {items.map((line) => {
            const hasHSN = items.some((ln) => (ln.hsn || '').toString().trim().length > 0);
            const t = computeLineTotals(line);
            return (
              <View key={line.id} style={printStyles.row}>
                <View style={[printStyles.tdBox, { flex: 2 }]}>
                  <Text style={printStyles.td}>{line.name}</Text>
                  {!!line.description && (
                    <Text style={printStyles.small}>{line.description}</Text>
                  )}
                </View>
                {hasHSN && (
                  <View style={[printStyles.tdBox, { flex: 1 }]}>
                    <Text style={printStyles.td}>{line.hsn}</Text>
                  </View>
                )}
                <View style={[printStyles.tdBox, { flex: 1 }]}>
                  <Text style={printStyles.td}>{formatNumber(line.gstRate)}%</Text>
                </View>
                <View style={[printStyles.tdBox, { flex: 1 }]}>
                  <Text style={printStyles.td}>{formatNumber(line.qty)}</Text>
                </View>
                <View style={[printStyles.tdBox, { flex: 1 }]}>
                  <Text style={printStyles.td}>{formatCurrency(line.rate, currencySymbol)}</Text>
                </View>
                {invoice.gstType === 'CGST_SGST' && !charges.hideTaxes ? (
                  <>
                    <View style={[printStyles.tdBox, { flex: 1 }]}>
                      <Text style={printStyles.td}>{formatCurrency(t.cgst, currencySymbol)}</Text>
                    </View>
                    <View style={[printStyles.tdBox, { flex: 1 }]}>
                      <Text style={printStyles.td}>{formatCurrency(t.sgst, currencySymbol)}</Text>
                    </View>
                  </>
                ) : (!charges.hideTaxes && (
                  <View style={[printStyles.tdBox, { flex: 1 }]}>
                    <Text style={printStyles.td}>{formatCurrency(t.igst, currencySymbol)}</Text>
                  </View>
                ))}
                <View style={[printStyles.tdBox, { flex: 1 }]}>
                  <Text style={printStyles.td}>{formatCurrency(t.total, currencySymbol)}</Text>
                </View>
              </View>
            );
          })}
        </View>

        {/* Totals area */}
        <View style={printStyles.block}>
          <View style={printStyles.totals}>
            <View style={printStyles.totalsRow}><Text style={printStyles.totalsLabel}>Subtotal</Text><Text style={printStyles.totalsValue}>{formatCurrency(summary.subtotal, currencySymbol)}</Text></View>
            {!charges.hideTaxes && (
              <>
                {invoice.gstType === 'CGST_SGST' ? (
                  <>
                    <View style={printStyles.totalsRow}><Text style={printStyles.totalsLabel}>CGST</Text><Text style={printStyles.totalsValue}>{formatCurrency(summary.cgst, currencySymbol)}</Text></View>
                    <View style={printStyles.totalsRow}><Text style={printStyles.totalsLabel}>SGST</Text><Text style={printStyles.totalsValue}>{formatCurrency(summary.sgst, currencySymbol)}</Text></View>
                  </>
                ) : (
                  <View style={printStyles.totalsRow}><Text style={printStyles.totalsLabel}>IGST</Text><Text style={printStyles.totalsValue}>{formatCurrency(summary.igst, currencySymbol)}</Text></View>
                )}
              </>
            )}
            {(parseFloat(charges.addlCharges) || 0) > 0 && (
              <View style={printStyles.totalsRow}><Text style={printStyles.totalsLabel}>Additional Charges</Text><Text style={printStyles.totalsValue}>{formatCurrency(charges.addlCharges, currencySymbol)}</Text></View>
            )}
            {(parseFloat(charges.discount) || 0) > 0 && (
              <View style={printStyles.totalsRow}><Text style={printStyles.totalsLabel}>Discount</Text><Text style={printStyles.totalsValue}>-{formatCurrency(charges.discount, currencySymbol)}</Text></View>
            )}
            {charges.summarizeQty && (
              <View style={printStyles.totalsRow}><Text style={printStyles.totalsLabel}>Total Quantity</Text><Text style={printStyles.totalsValue}>{formatNumber(summary.qtyTotal)}</Text></View>
            )}
          </View>
          <View style={printStyles.grandRow}><Text style={printStyles.grandLabel}>Grand Total ({invoice.currency})</Text><Text style={printStyles.grandValue}>{formatCurrency(summary.total, currencySymbol)}</Text></View>
          {invoice.showTotalWords && (
            <Text style={[printStyles.small, { marginTop: 6 }]}>Amount in words: {numberToWords(Math.round(summary.total))}</Text>
          )}
        </View>

        {/* Notes and Terms */}
        {(notes || terms || contactDetails) && (
          <View style={printStyles.block}>
            {!!terms && (<Text style={printStyles.blockTitle}>Terms</Text>)}
            {!!terms && (<Text style={printStyles.small}>{terms}</Text>)}
            {!!notes && (<Text style={[printStyles.blockTitle, { marginTop: 8 }]}>Notes</Text>)}
            {!!notes && (<Text style={printStyles.small}>{notes}</Text>)}
            {!!contactDetails && (<Text style={[printStyles.blockTitle, { marginTop: 8 }]}>Contact</Text>)}
            {!!contactDetails && (<Text style={printStyles.small}>{contactDetails}</Text>)}
            {(payment.bankName || payment.accountNo || payment.ifsc || payment.upiId) && (
              <>
                <Text style={[printStyles.blockTitle, { marginTop: 8 }]}>Payment Details</Text>
                {!!payment.bankName && (<Text style={printStyles.small}>Bank: {payment.bankName}</Text>)}
                {!!payment.accountNo && (<Text style={printStyles.small}>Account No: {payment.accountNo}</Text>)}
                {!!payment.ifsc && (<Text style={printStyles.small}>IFSC: {payment.ifsc}</Text>)}
                {!!payment.upiId && (<Text style={printStyles.small}>UPI: {payment.upiId}</Text>)}
              </>
            )}
          </View>
        )}

        <View style={printStyles.signatureRow}>
          <View style={printStyles.sigBlock}>
            <Text style={printStyles.small}>Authorized Signatory</Text>
            <View style={printStyles.sigLine} />
          </View>
          <View style={printStyles.sigBlock}>
            <Text style={printStyles.small}>Receiver’s Signature</Text>
            <View style={printStyles.sigLine} />
          </View>
        </View>
        {/* Footer note for PDF */}
        <Text style={printStyles.footerText}>Invoice generated by modernmunshiji.com</Text>
      </View>
    );
  };

  // Memoize the entire print preview tree using deferred snapshots
  const memoPreview = useMemo(() => renderPrintInvoice(), [
    template,
    dInvoice,
    dSeller,
    dClient,
    dShipping,
    dItems,
    dCharges,
    dNotes,
    dTerms,
    dContactDetails,
    dPayment,
    dSummary,
    logoUri,
  ]);



  const renderHeader = () => (
    <View style={styles.heroSection}>
      <View style={styles.heroContent}>
        <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
          <Text style={styles.heroHeading}>Free Invoice Generator - Online Invoice Bill Maker</Text>
          <TouchableOpacity style={styles.primaryButton} onPress={exportPDF}>
            <Ionicons name="download-outline" size={16} color="#ffffff" />
            <Text style={styles.primaryButtonText}>Download PDF</Text>
          </TouchableOpacity>
        </View>
        <View style={{ marginTop: 10, gap: 8 }}>
          <Text style={styles.heroDescription}>
            Modern Munshiji free online invoice generator helps you create, download and send professional invoices.
            Simply add invoice details, customize your invoice using templates and get paid faster. Modern Munshiji
            invoice generator is one of the easiest and simplest tools to create invoices for free.
          </Text>
        </View>
      </View>
    </View>
  );

  const renderTopMeta = () => (
    <SectionCard
      title="Invoice Details"
      right={isWeb && (<LogoPicker currentLogoUri={logoUri} />)}
    >
      <View style={[styles.inputRow, isMobile && { flexDirection: 'column' }]}>
        <View style={[styles.half, { gap: 12 }]}>
          <Text style={styles.label}>Invoice No</Text>
          <TextInput
            style={styles.input}
            value={invoice.number}
            onChangeText={(v) => setInvoice(p => ({ ...p, number: v.toUpperCase() }))}
            placeholder="A00001"
            autoCapitalize="characters"
            autoCorrect={false}
            spellCheck={false}
            // Improve web typing behavior
            autoComplete="off"
            selectTextOnFocus
            maxLength={24}
          />
        </View>
        <View style={[styles.half, { gap: 12 }]}>
          <Text style={styles.label}>Invoice Date</Text>
          <TextInput style={[styles.input, !!errors.invoice?.invoiceDate && styles.inputError]} value={invoice.invoiceDate} onChangeText={(v) => setInvoice(p => ({ ...p, invoiceDate: v }))} placeholder="YYYY-MM-DD" autoCorrect={false} spellCheck={false} autoComplete="off" />
          {!!errors.invoice?.invoiceDate && (<Text style={styles.errorText}>{errors.invoice.invoiceDate}</Text>)}
          {invoice.showDueDate && (
            <View style={{ marginTop: 8 }}>
              <Text style={styles.label}>Due Date</Text>
              <TextInput style={[styles.input, !!errors.invoice?.dueDate && styles.inputError]} value={invoice.dueDate} onChangeText={(v) => setInvoice(p => ({ ...p, dueDate: v }))} placeholder="YYYY-MM-DD" autoCorrect={false} spellCheck={false} autoComplete="off" />
              {!!errors.invoice?.dueDate && (<Text style={styles.errorText}>{errors.invoice.dueDate}</Text>)}
            </View>
          )}
          <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: 8, gap: 8 }}>
            <TouchableOpacity style={styles.checkbox} onPress={() => setInvoice(p => ({ ...p, showDueDate: !p.showDueDate }))}>
              <Text style={styles.checkboxIcon}>{invoice.showDueDate ? '✓' : ''}</Text>
            </TouchableOpacity>
            <Text style={styles.helperText}>Add due date</Text>
          </View>
        </View>
      </View>
    </SectionCard>
  );

  const renderParties = () => (
    <View style={[styles.calculatorRow, isMobile && { flexDirection: 'column' }]}>
      <View style={[styles.formContent, isMobile ? { width: '100%' } : { flex: 0.5 }]}>
        <SectionCard title="Billed By (Your Details)">
          {renderPartyForm(seller, setSeller, 'seller')}
        </SectionCard>
      </View>
      <View style={[styles.summaryPanel, isMobile ? { width: '100%', marginTop: 16 } : { flex: 0.5 }]}>
        <SectionCard title="Billed To (Client Details)">
          {renderPartyForm(client, setClient, 'client')}
        </SectionCard>
      </View>
    </View>
  );

  const renderPartyForm = (party, setParty, keyName) => (
    <View style={{ gap: 12 }}>
      <View style={styles.inputRow}>
        <View style={styles.half}>
          <Text style={styles.label}>Country</Text>
          {isWeb ? React.createElement('select', {
            value: party.country,
            onChange: (e) => setParty(p => ({ ...p, country: e.target.value })),
            style: { padding: 10, height: 40, borderRadius: 8, border: '1px solid #e5e7eb', backgroundColor: '#f8fafc', color: '#0f172a' },
          }, countries.map((c) => React.createElement('option', { key: c, value: c }, c))) : (
            <View style={{ borderWidth: 1, borderColor: '#e5e7eb', backgroundColor: '#f8fafc', borderRadius: 8 }}>
              <Picker selectedValue={party.country} onValueChange={(v) => setParty(p => ({ ...p, country: v }))}>
                {countries.map((c) => (<Picker.Item key={c} label={c} value={c} />))}
              </Picker>
            </View>
          )}
        </View>
        <View style={styles.half}>
          <Text style={styles.label}>Business Name</Text>
          <TextInput style={[styles.input, (errors[keyName]?.businessName) && styles.inputError]} value={party.businessName} onChangeText={(v) => setParty(p => ({ ...p, businessName: v }))} placeholder="Your Business Name" autoCorrect={false} spellCheck={false} autoComplete="off" />
          {!!errors[keyName]?.businessName && (<Text style={styles.errorText}>{errors[keyName].businessName}</Text>)}
        </View>
      </View>
      <View style={styles.inputRow}>
        <View style={styles.half}>
          <Text style={styles.label}>GSTIN</Text>
          <TextInput style={[styles.input, (errors[keyName]?.gstin) && styles.inputError]} value={party.gstin} onChangeText={(v) => setParty(p => ({ ...p, gstin: v }))} placeholder="22AAAAA0000A1Z5" autoCorrect={false} spellCheck={false} autoComplete="off" />
          {!!errors[keyName]?.gstin && (<Text style={styles.errorText}>{errors[keyName].gstin}</Text>)}
        </View>
        <View style={styles.half}>
          <Text style={styles.label}>Address</Text>
          <TextInput style={styles.input} value={party.address} onChangeText={(v) => setParty(p => ({ ...p, address: v }))} placeholder="Address" autoCorrect={false} spellCheck={false} autoComplete="off" />
        </View>
      </View>
      <View style={styles.inputRow}>
        <View style={styles.half}>
          <Text style={styles.label}>City</Text>
          <TextInput style={styles.input} value={party.city} onChangeText={(v) => setParty(p => ({ ...p, city: v }))} placeholder="City" autoCorrect={false} spellCheck={false} autoComplete="off" />
        </View>
        <View style={styles.half}>
          <Text style={styles.label}>Postal Code</Text>
          <TextInput style={[styles.input, (errors[keyName]?.postalCode) && styles.inputError]} value={party.postalCode} onChangeText={(v) => setParty(p => ({ ...p, postalCode: v }))} placeholder="PIN / ZIP" autoCorrect={false} spellCheck={false} autoComplete="off" />
          {!!errors[keyName]?.postalCode && (<Text style={styles.errorText}>{errors[keyName].postalCode}</Text>)}
        </View>
      </View>
      <View style={styles.inputRow}>
        <View style={styles.half}>
          <Text style={styles.label}>Email</Text>
          <TextInput style={[styles.input, (errors[keyName]?.email) && styles.inputError]} value={party.email} onChangeText={(v) => setParty(p => ({ ...p, email: v }))} placeholder="email@example.com" autoCorrect={false} spellCheck={false} autoComplete="off" />
          {!!errors[keyName]?.email && (<Text style={styles.errorText}>{errors[keyName].email}</Text>)}
        </View>
        <View style={styles.half}>
          <Text style={styles.label}>PAN</Text>
          <TextInput style={[styles.input, (errors[keyName]?.pan) && styles.inputError]} value={party.pan} onChangeText={(v) => setParty(p => ({ ...p, pan: v }))} placeholder="ABCDE1234F" autoCorrect={false} spellCheck={false} autoComplete="off" />
          {!!errors[keyName]?.pan && (<Text style={styles.errorText}>{errors[keyName].pan}</Text>)}
        </View>
      </View>
    </View>
  );

  const renderShippingToggle = () => (
    <View style={{ marginTop: 8 }}>
      <TouchableOpacity style={styles.outlineButton} onPress={() => setShowShipping(s => !s)}>
        <Ionicons name="cube-outline" size={16} color="#4F46E5" />
        <Text style={styles.outlineButtonText}>{showShipping ? 'Hide' : 'Add'} Shipping Details</Text>
      </TouchableOpacity>
      {showShipping && (
        <View style={[styles.card, { marginTop: 12 }]}>
          <View style={styles.inputRow}>
            <View style={styles.half}>
              <Text style={styles.label}>Address</Text>
              <TextInput style={styles.input} value={shipping.address} onChangeText={(v) => setShipping(p => ({ ...p, address: v }))} autoCorrect={false} spellCheck={false} autoComplete="off" />
            </View>
            <View style={styles.half}>
              <Text style={styles.label}>City</Text>
              <TextInput style={styles.input} value={shipping.city} onChangeText={(v) => setShipping(p => ({ ...p, city: v }))} autoCorrect={false} spellCheck={false} autoComplete="off" />
            </View>
          </View>
          <View style={styles.inputRow}>
            <View style={styles.half}>
              <Text style={styles.label}>Postal Code</Text>
              <TextInput style={[styles.input, !!errors.shipping?.postalCode && styles.inputError]} value={shipping.postalCode} onChangeText={(v) => setShipping(p => ({ ...p, postalCode: v }))} autoCorrect={false} spellCheck={false} autoComplete="off" />
              {!!errors.shipping?.postalCode && (<Text style={styles.errorText}>{errors.shipping.postalCode}</Text>)}
            </View>
          </View>
        </View>
      )}
    </View>
  );

  const renderItemsHeader = () => (
    <View style={[styles.itemHeaderRow]}>
      <Text style={[styles.itemHeaderCell, { flex: 2 }]}>Item</Text>
      <Text style={[styles.itemHeaderCell, { flex: 1 }]}>HSN/SAC</Text>
      <Text style={[styles.itemHeaderCell, { flex: 1 }]}>GST Rate</Text>
      <Text style={[styles.itemHeaderCell, { flex: 1 }]}>Qty</Text>
      <Text style={[styles.itemHeaderCell, { flex: 1 }]}>Rate</Text>
      {!charges.hideTaxes && invoice.gstType === 'CGST_SGST' && (
        <>
          <Text style={[styles.itemHeaderCell, { flex: 1 }]}>CGST</Text>
          <Text style={[styles.itemHeaderCell, { flex: 1 }]}>SGST</Text>
        </>
      )}
      {!charges.hideTaxes && invoice.gstType === 'IGST' && (
        <Text style={[styles.itemHeaderCell, { flex: 1 }]}>IGST</Text>
      )}
      <Text style={[styles.itemHeaderCell, { flex: 1 }]}>Total</Text>
      <Text style={[styles.itemHeaderCell, { flex: 0.7 }]}>Actions</Text>
    </View>
  );

  const renderItemRow = (line) => {
    const t = computeLineTotals(line);
    return (
      <View key={line.id} style={[styles.itemRow]}>
        <View style={[styles.itemCell, { flex: 2 }]}>
          <TextInput style={[styles.input, !!(errors.items?.[line.id]?.name) && styles.inputError]} value={line.name} onChangeText={(v) => updateItem(line.id, 'name', v)} placeholder="Item Name" autoCorrect={false} spellCheck={false} autoComplete="off" />
          {!!(errors.items?.[line.id]?.name) && (<Text style={styles.errorText}>{errors.items[line.id].name}</Text>)}
          <View style={{ flexDirection: 'row', gap: 8, marginTop: 6 }}>
            <TouchableOpacity
              style={styles.link}
              onPress={() => updateItem(line.id, 'showDescription', !line.showDescription)}
            >
              <Text style={styles.linkText}>{line.showDescription ? 'Hide Description' : 'Add Description'}</Text>
            </TouchableOpacity>
          </View>
          {line.showDescription && (
            <View style={{ marginTop: 8 }}>
              <TextInput
                style={[styles.input, { height: 60 }]}
                multiline
                value={line.description}
                onChangeText={(v) => updateItem(line.id, 'description', v)}
                placeholder="Add description..."
              />
            </View>
          )}
        </View>
        <View style={[styles.itemCell, { flex: 1 }]}>
          <TextInput style={[styles.input, !!(errors.items?.[line.id]?.hsn) && styles.inputError]} value={line.hsn} onChangeText={(v) => updateItem(line.id, 'hsn', v)} placeholder="HSN/SAC" autoCorrect={false} spellCheck={false} autoComplete="off" />
          {!!(errors.items?.[line.id]?.hsn) && (<Text style={styles.errorText}>{errors.items[line.id].hsn}</Text>)}
        </View>
        <View style={[styles.itemCell, { flex: 1 }]}>
          <TextInput style={[styles.input, !!(errors.items?.[line.id]?.gstRate) && styles.inputError]} value={String(line.gstRate)} onChangeText={(v) => updateItem(line.id, 'gstRate', v)} keyboardType="numeric" placeholder="18" autoCorrect={false} spellCheck={false} autoComplete="off" />
          {!!(errors.items?.[line.id]?.gstRate) && (<Text style={styles.errorText}>{errors.items[line.id].gstRate}</Text>)}
        </View>
        <View style={[styles.itemCell, { flex: 1 }]}>
          <TextInput style={[styles.input, !!(errors.items?.[line.id]?.qty) && styles.inputError]} value={String(line.qty)} onChangeText={(v) => updateItem(line.id, 'qty', v)} keyboardType="numeric" placeholder="1" autoCorrect={false} spellCheck={false} autoComplete="off" />
          {!!(errors.items?.[line.id]?.qty) && (<Text style={styles.errorText}>{errors.items[line.id].qty}</Text>)}
        </View>
        <View style={[styles.itemCell, { flex: 1 }]}>
          <TextInput style={[styles.input, !!(errors.items?.[line.id]?.rate) && styles.inputError]} value={String(line.rate)} onChangeText={(v) => updateItem(line.id, 'rate', v)} keyboardType="numeric" placeholder="1" autoCorrect={false} spellCheck={false} autoComplete="off" />
          {!!(errors.items?.[line.id]?.rate) && (<Text style={styles.errorText}>{errors.items[line.id].rate}</Text>)}
        </View>
        {!charges.hideTaxes && invoice.gstType === 'CGST_SGST' && (
          <>
            <View style={[styles.itemCell, { flex: 1 }]}>
              <Text style={styles.itemValue}>{formatCurrency(t.cgst, currencySymbol)}</Text>
            </View>
            <View style={[styles.itemCell, { flex: 1 }]}>
              <Text style={styles.itemValue}>{formatCurrency(t.sgst, currencySymbol)}</Text>
            </View>
          </>
        )}
        {!charges.hideTaxes && invoice.gstType === 'IGST' && (
          <View style={[styles.itemCell, { flex: 1 }]}>
            <Text style={styles.itemValue}>{formatCurrency(t.igst, currencySymbol)}</Text>
          </View>
        )}
        <View style={[styles.itemCell, { flex: 1 }]}>
          <Text style={styles.itemValue}>{formatCurrency(t.total, currencySymbol)}</Text>
        </View>
        <View style={[styles.itemCell, { flex: 0.7, flexDirection: 'row', gap: 6 }]}>
          <TouchableOpacity style={styles.iconButton} onPress={() => duplicateLine(line.id)}>
            <Ionicons name="copy-outline" size={16} color="#4F46E5" />
          </TouchableOpacity>
          <TouchableOpacity style={styles.iconButton} onPress={() => removeLine(line.id)}>
            <Ionicons name="trash-outline" size={16} color="#EF4444" />
          </TouchableOpacity>
        </View>
      </View>
    );
  };

  const renderItems = () => (
    <View style={styles.card}>
      <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
        <Text style={styles.cardTitle}>Items</Text>
        <View style={{ flexDirection: 'row', gap: 8, alignItems: 'center' }}>
          <Text style={styles.helperText}>GST Type</Text>
          <TouchableOpacity style={[styles.outlineButton, invoice.gstType === 'CGST_SGST' && styles.outlineButtonActive]} onPress={() => setInvoice(p => ({ ...p, gstType: 'CGST_SGST' }))}><Text style={styles.outlineButtonText}>CGST/SGST</Text></TouchableOpacity>
          <TouchableOpacity style={[styles.outlineButton, invoice.gstType === 'IGST' && styles.outlineButtonActive]} onPress={() => setInvoice(p => ({ ...p, gstType: 'IGST' }))}><Text style={styles.outlineButtonText}>IGST</Text></TouchableOpacity>
        </View>
      </View>
      {renderItemsHeader()}
      {items.map(renderItemRow)}
      <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
        <TouchableOpacity style={styles.link} onPress={addNewLine}><Text style={styles.linkText}>Add New Line</Text></TouchableOpacity>
        <View style={{ flexDirection: 'row', gap: 8 }}>
          <Text style={styles.helperText}>Currency</Text>
          <TouchableOpacity style={[styles.outlineButton, invoice.currency === 'INR' && styles.outlineButtonActive]} onPress={() => setInvoice(p => ({ ...p, currency: 'INR' }))}><Text style={styles.outlineButtonText}>INR</Text></TouchableOpacity>
          <TouchableOpacity style={[styles.outlineButton, invoice.currency === 'USD' && styles.outlineButtonActive]} onPress={() => setInvoice(p => ({ ...p, currency: 'USD' }))}><Text style={styles.outlineButtonText}>USD</Text></TouchableOpacity>
          <TouchableOpacity style={[styles.outlineButton, invoice.currency === 'EUR' && styles.outlineButtonActive]} onPress={() => setInvoice(p => ({ ...p, currency: 'EUR' }))}><Text style={styles.outlineButtonText}>EUR</Text></TouchableOpacity>
        </View>
      </View>
    </View>
  );

  const renderTotals = () => (
    <View style={[styles.card]}>
      <Text style={styles.cardTitle}>Totals</Text>
      <View style={{ gap: 8 }}>
        <View style={styles.summaryRow}><Text style={styles.summaryLabel}>Amount</Text><Text style={styles.summaryValue}>{formatCurrency(summary.subtotal, currencySymbol)}</Text></View>
        {!charges.hideTaxes && (
          <>
            <View style={styles.summaryRow}><Text style={styles.summaryLabel}>CGST</Text><Text style={styles.summaryValue}>{formatCurrency(summary.cgst, currencySymbol)}</Text></View>
            <View style={styles.summaryRow}><Text style={styles.summaryLabel}>SGST</Text><Text style={styles.summaryValue}>{formatCurrency(summary.sgst, currencySymbol)}</Text></View>
            <View style={styles.summaryRow}><Text style={styles.summaryLabel}>IGST</Text><Text style={styles.summaryValue}>{formatCurrency(summary.igst, currencySymbol)}</Text></View>
          </>
        )}
        <View style={{ height: 8 }} />
        <View style={styles.summaryRow}><Text style={styles.summaryLabel}>Additional Charges</Text><TextInput style={[styles.input, { width: 120 }]} value={String(charges.addlCharges)} onChangeText={(v) => setCharges(p => ({ ...p, addlCharges: v }))} keyboardType="numeric" autoCorrect={false} spellCheck={false} autoComplete="off" /></View>
        <View style={styles.summaryRow}><Text style={styles.summaryLabel}>Discount</Text><TextInput style={[styles.input, { width: 120 }]} value={String(charges.discount)} onChangeText={(v) => setCharges(p => ({ ...p, discount: v }))} keyboardType="numeric" autoCorrect={false} spellCheck={false} autoComplete="off" /></View>
        <View style={{ height: 8 }} />
        <View style={{ flexDirection: 'row', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <TouchableOpacity style={styles.outlineButton} onPress={() => setCharges(p => ({ ...p, hideTaxes: !p.hideTaxes }))}><Text style={styles.outlineButtonText}>{charges.hideTaxes ? 'Show Taxes' : 'Hide Taxes'}</Text></TouchableOpacity>
          <Text style={styles.helperText}>Round</Text>
          <TouchableOpacity style={[styles.outlineButton, charges.roundMode === 'none' && styles.outlineButtonActive]} onPress={() => setCharges(p => ({ ...p, roundMode: 'none' }))}><Text style={styles.outlineButtonText}>None</Text></TouchableOpacity>
          <TouchableOpacity style={[styles.outlineButton, charges.roundMode === 'round_up' && styles.outlineButtonActive]} onPress={() => setCharges(p => ({ ...p, roundMode: 'round_up' }))}><Text style={styles.outlineButtonText}>Round Up</Text></TouchableOpacity>
          <TouchableOpacity style={[styles.outlineButton, charges.roundMode === 'round_down' && styles.outlineButtonActive]} onPress={() => setCharges(p => ({ ...p, roundMode: 'round_down' }))}><Text style={styles.outlineButtonText}>Round Down</Text></TouchableOpacity>
          <TouchableOpacity style={styles.outlineButton} onPress={() => setCharges(p => ({ ...p, summarizeQty: !p.summarizeQty }))}><Text style={styles.outlineButtonText}>{charges.summarizeQty ? 'Hide Qty Summary' : 'Summarize Total Quantity'}</Text></TouchableOpacity>
        </View>
        <View style={{ height: 8 }} />
        <View style={styles.totalRow}><Text style={styles.totalLabel}>Total ({invoice.currency})</Text><Text style={styles.totalValue}>{formatCurrency(summary.total, currencySymbol)}</Text></View>
        {invoice.showTotalWords && (
          <Text style={styles.helperText}>In words: {numberToWords(Math.round(summary.total))}</Text>
        )}
        <View style={{ flexDirection: 'row', gap: 12, marginTop: 8, flexWrap: 'wrap' }}>
          <TouchableOpacity style={styles.outlineButton} onPress={() => setInvoice(p => ({ ...p, showTotalWords: !p.showTotalWords }))}><Text style={styles.outlineButtonText}>{invoice.showTotalWords ? 'Hide Total In Words' : 'Show Total In Words'}</Text></TouchableOpacity>
        </View>
      </View>
    </View>
  );

  const renderExtras = () => (
    <View style={styles.card}>
      <Text style={styles.cardTitle}>Additional Information</Text>
      <View style={styles.inputRow}>
        <View style={styles.half}>
          <Text style={styles.label}>Terms & Conditions</Text>
          <TextInput style={[styles.input, { height: 80 }]} multiline value={terms} onChangeText={setTerms} placeholder="Add terms..." />
        </View>
        <View style={styles.half}>
          <Text style={styles.label}>Notes</Text>
          <TextInput style={[styles.input, { height: 80 }]} multiline value={notes} onChangeText={setNotes} placeholder="Add notes..." />
        </View>
      </View>
      <View style={styles.inputRow}>
        <View style={styles.half}>
          <Text style={styles.label}>Contact Details</Text>
          <TextInput style={[styles.input, { height: 80 }]} multiline value={contactDetails} onChangeText={setContactDetails} placeholder="Phone, email, address..." />
        </View>
        <View style={styles.half}>
          <Text style={styles.label}>Attachments</Text>
          <TouchableOpacity style={styles.outlineButton}><Text style={styles.outlineButtonText}>Add Attachments</Text></TouchableOpacity>
        </View>
      </View>
      <View style={styles.inputRow}>
        <View style={styles.half}>
          <Text style={styles.label}>Payment Details</Text>
          <View style={{ gap: 8 }}>
            <TextInput style={styles.input} value={payment.bankName} onChangeText={(v) => setPayment(p => ({ ...p, bankName: v }))} placeholder="Bank Name" />
            <TextInput style={styles.input} value={payment.accountNo} onChangeText={(v) => setPayment(p => ({ ...p, accountNo: v }))} placeholder="Account Number" />
            <TextInput style={[styles.input, !!errors.payment?.ifsc && styles.inputError]} value={payment.ifsc} onChangeText={(v) => setPayment(p => ({ ...p, ifsc: v }))} placeholder="IFSC" />
            {!!errors.payment?.ifsc && (<Text style={styles.errorText}>{errors.payment.ifsc}</Text>)}
            <TextInput style={[styles.input, !!errors.payment?.upiId && styles.inputError]} value={payment.upiId} onChangeText={(v) => setPayment(p => ({ ...p, upiId: v }))} placeholder="UPI ID" />
            {!!errors.payment?.upiId && (<Text style={styles.errorText}>{errors.payment.upiId}</Text>)}
          </View>
        </View>
        <View style={styles.half} />
      </View>
      <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginTop: 12 }}>
        <View style={{ flexDirection: 'row', gap: 10 }}>
          <TouchableOpacity style={styles.primaryButton} onPress={handleSaveInvoice} disabled={saving}>
            {saving ? (
              <ActivityIndicator size="small" color="#ffffff" />
            ) : (
              <Ionicons name="save-outline" size={16} color="#ffffff" />
            )}
            <Text style={styles.primaryButtonText}>{saving ? (editingInvoiceId ? 'Updating…' : 'Saving…') : (editingInvoiceId ? 'Update' : 'Save')}</Text>
          </TouchableOpacity>
          <TouchableOpacity style={styles.primaryButton} onPress={exportPDF}>
            <Ionicons name="download-outline" size={16} color="#ffffff" />
            <Text style={styles.primaryButtonText}>Download PDF</Text>
          </TouchableOpacity>
        </View>
        <View style={{ minWidth: 160, alignItems: 'flex-end' }}>
          {saving && (
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
              <ActivityIndicator size="small" color="#4F46E5" />
              <Text style={styles.helperText}>Saving invoice…</Text>
            </View>
          )}
          {!!saveMessage && !saving && (<Text style={styles.helperText}>{saveMessage}</Text>)}
        </View>
      </View>
    </View>
  );

  return (
    <ScrollView style={styles.container}>
      <View nativeID="invoice-root" ref={invoiceRef}>
        {renderHeader()}
        <View style={[styles.calculatorContainer]}>
          <View style={[styles.calculatorRow, isMobile && { flexDirection: 'column' }]}>
            <View style={[styles.formContent, !isMobile && { flex: leftPanelShare }, isMobile && { width: '100%' }]}>
              {renderTopMeta()}
              {renderParties()}
              {renderShippingToggle()}
              {renderItems()}
              {renderTotals()}
              {renderExtras()}
              {/* Saved Invoices section removed per request */}
              <SectionCard title="Why Choose Modern Munshiji Invoice Generator">
                <View style={{ gap: 8 }}>
                  <Text style={styles.helperText}>• 100% Free online invoice maker with instant PDF download.</Text>
                  <Text style={styles.helperText}>• Supports GST (IGST/CGST/SGST) and HSN/SAC for Indian businesses.</Text>
                  <Text style={styles.helperText}>• Add company logo, billing and shipping addresses, and item descriptions.</Text>
                  <Text style={styles.helperText}>• Real-time invoice preview for zero surprises when you export.</Text>
                  <Text style={styles.helperText}>• No signup required and your data stays in your browser.</Text>
                  <Text style={styles.helperText}>• Designed for freelancers, SMEs, and startups to create professional invoices fast.</Text>
                </View>
              </SectionCard>
              {/* SEO content blocks below the generator */}
              <SectionCard title="How to Use Modern Munshiji Invoice Generator">
                <View style={{ gap: 8 }}>
                  <Text style={styles.helperText}>• Enter your business and client details.</Text>
                  <Text style={styles.helperText}>• Add items, HSN/SAC, GST rate, quantity and rate.</Text>
                  <Text style={styles.helperText}>• Optionally upload your company logo.</Text>
                  <Text style={styles.helperText}>• Choose tax type (CGST/SGST or IGST) and add charges/discounts.</Text>
                  <Text style={styles.helperText}>• Preview the invoice live on the right.</Text>
                  <Text style={styles.helperText}>• Click Download PDF to save or share.</Text>
                </View>
              </SectionCard>
              <SectionCard title="Invoice Generator FAQs">
                <View style={{ gap: 10 }}>
                  <View>
                    <Text style={styles.label}>Is it free?</Text>
                    <Text style={styles.helperText}>Yes, Modern Munshiji invoice generator is completely free to use.</Text>
                  </View>
                  <View>
                    <Text style={styles.label}>Can I add GST?</Text>
                    <Text style={styles.helperText}>Yes, set GST rates per item and choose IGST or CGST/SGST.</Text>
                  </View>
                  <View>
                    <Text style={styles.label}>Can I upload my logo?</Text>
                    <Text style={styles.helperText}>Use the logo picker in Invoice Details to add your branding.</Text>
                  </View>
                  <View>
                    <Text style={styles.label}>Can I download as PDF?</Text>
                    <Text style={styles.helperText}>Yes, use the Download PDF button in the top hero.</Text>
                  </View>
                  <View>
                    <Text style={styles.label}>Do you store my data?</Text>
                    <Text style={styles.helperText}>No, invoices are generated in your browser and not stored on our servers.</Text>
                  </View>
                </View>
              </SectionCard>
            </View>
            <View style={[styles.summaryPanel, !isMobile && { flex: rightPanelShare }, isMobile && { width: '100%', marginTop: 16 }]}>
              <SectionCard title="Live Preview" right={(
                <View style={{ flexDirection: 'column', gap: 6, alignItems: 'flex-end' }}>
                  <Text style={[styles.helperText, { marginBottom: 2 }]}>Select Template</Text>
                  <View style={{ flexDirection: 'row', gap: 8, alignItems: 'center' }}>
                    <TouchableOpacity style={[styles.outlineButton, template === 'classic' && styles.outlineButtonActive]} onPress={() => setTemplate('classic')}><Text style={styles.outlineButtonText}>Template 1</Text></TouchableOpacity>
                    <TouchableOpacity style={[styles.outlineButton, template === 'emerald' && styles.outlineButtonActive]} onPress={() => setTemplate('emerald')}><Text style={styles.outlineButtonText}>Template 2</Text></TouchableOpacity>
                    <TouchableOpacity style={[styles.outlineButton, template === 'minimal' && styles.outlineButtonActive]} onPress={() => setTemplate('minimal')}><Text style={styles.outlineButtonText}>Template 3</Text></TouchableOpacity>
                    <TouchableOpacity style={[styles.outlineButton, template === 'slate' && styles.outlineButtonActive]} onPress={() => setTemplate('slate')}><Text style={styles.outlineButtonText}>Template 4</Text></TouchableOpacity>
                  </View>
                </View>
              )}>
                <View style={{ backgroundColor: 'transparent', alignItems: 'flex-start', overflow: 'hidden', width: '100%' }}>
                  {/* Scale dimensions so the element width matches the available card width and anchor top-left on web */}
                  <View style={{ width: 794 * previewScale, minHeight: 1123 * previewScale }}>
                    <View style={[{ transform: [{ scale: previewScale }] }, Platform.OS === 'web' ? { transformOrigin: 'top left' } : null]}>
                      {memoPreview}
                    </View>
                  </View>
                </View>
              </SectionCard>
            </View>
          </View>
        </View>
      </View>
      {isWeb && (
        <View nativeID="invoice-print" style={{ position: 'absolute', left: -10000, top: 0 }} pointerEvents="none" aria-hidden="true" tabIndex={-1} importantForAccessibility="no-hide-descendants">
          {memoPreview}
        </View>
      )}
      <Footer />
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#f8fafc' },
  heroSection: {
    backgroundColor: '#4F46E5',
    paddingVertical: 40,
    paddingHorizontal: 30,
  },
  heroContent: { width: '100%', alignSelf: 'stretch' },
  heroTitle: { fontSize: 26, fontWeight: 'bold', color: '#ffffff' },
  heroHeading: { fontSize: 30, fontWeight: '800', color: '#ffffff' },
  heroDescription: { fontSize: 14, color: '#e0e7ff', lineHeight: 20 },
  calculatorContainer: { width: '100%', alignSelf: 'stretch', paddingHorizontal: 30, paddingVertical: 16 },
  calculatorRow: { flexDirection: 'row', gap: 24 },
  formContent: { flex: 0.7, gap: 12 },
  summaryPanel: { flex: 0.3, alignItems: 'flex-start' },
  card: { backgroundColor: '#ffffff', borderRadius: 12, padding: 16, borderWidth: 1, borderColor: '#e5e7eb', marginBottom: 16 },
  cardHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 },
  cardTitle: { fontSize: 16, fontWeight: '600', color: '#0f172a' },
  inputRow: { flexDirection: 'row', gap: 16 },
  half: { flex: 1 },
  label: { fontSize: 14, fontWeight: '600', color: '#0f172a', marginBottom: 6 },
  input: { backgroundColor: '#f8fafc', borderRadius: 8, borderWidth: 1, borderColor: '#e5e7eb', paddingHorizontal: 12, height: 40, fontSize: 14, color: '#0f172a' },
  inputError: { borderColor: '#EF4444', backgroundColor: '#fff1f2' },
  errorText: { color: '#EF4444', fontSize: 12, marginTop: 4 },
  checkbox: { width: 18, height: 18, borderRadius: 4, borderWidth: 1, borderColor: '#e5e7eb', alignItems: 'center', justifyContent: 'center' },
  checkboxIcon: { fontSize: 12, color: '#0f172a' },
  helperText: { fontSize: 12, color: '#64748b' },
  link: { paddingVertical: 6, paddingHorizontal: 10, backgroundColor: '#4F46E510', borderRadius: 6, borderWidth: 1, borderColor: '#e5e7eb' },
  linkText: { color: '#4F46E5', fontWeight: '600', fontSize: 12 },
  primaryButton: { flexDirection: 'row', alignItems: 'center', gap: 8, backgroundColor: '#4F46E5', paddingVertical: 8, paddingHorizontal: 12, borderRadius: 8 },
  primaryButtonText: { color: '#ffffff', fontWeight: '600' },
  outlineButton: { flexDirection: 'row', alignItems: 'center', gap: 8, paddingVertical: 6, paddingHorizontal: 10, borderRadius: 8, borderWidth: 1, borderColor: '#4F46E5' },
  outlineButtonActive: { backgroundColor: '#4F46E515' },
  outlineButtonText: { color: '#4F46E5', fontWeight: '600', fontSize: 12 },
  itemHeaderRow: { flexDirection: 'row', backgroundColor: '#4F46E5', borderTopLeftRadius: 8, borderTopRightRadius: 8, paddingVertical: 10, paddingHorizontal: 8, marginTop: 8 },
  itemHeaderCell: { color: '#ffffff', fontWeight: '600', fontSize: 12, textAlign: 'left' },
  itemRow: { flexDirection: 'row', backgroundColor: '#ffffff', borderBottomWidth: 1, borderColor: '#e5e7eb', paddingVertical: 8, paddingHorizontal: 8, alignItems: 'flex-start', gap: 6 },
  itemCell: { paddingHorizontal: 6, justifyContent: 'center' },
  itemValue: { fontSize: 14, color: '#0f172a' },
  iconButton: { padding: 6, borderRadius: 6, borderWidth: 1, borderColor: '#e5e7eb' },
  summaryRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
  summaryLabel: { fontSize: 14, color: '#334155' },
  summaryValue: { fontSize: 14, fontWeight: '600', color: '#0f172a' },
  totalRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingTop: 8 },
  totalLabel: { fontSize: 16, fontWeight: '600', color: '#0f172a' },
  totalValue: { fontSize: 18, fontWeight: 'bold', color: '#065f46' },
  // Saved Invoices table styles
  tableHeaderRow: { flexDirection: 'row', backgroundColor: '#f3f4f6', borderWidth: 1, borderColor: '#e5e7eb', borderTopLeftRadius: 8, borderTopRightRadius: 8, paddingVertical: 10, paddingHorizontal: 8 },
  tableHeaderCell: { color: '#0f172a', fontWeight: '700', fontSize: 12 },
  tableRow: { flexDirection: 'row', backgroundColor: '#ffffff', borderLeftWidth: 1, borderRightWidth: 1, borderBottomWidth: 1, borderColor: '#e5e7eb', paddingVertical: 8, paddingHorizontal: 8, alignItems: 'center' },
  tableCell: { fontSize: 12, color: '#0f172a' },
  pill: { paddingVertical: 4, paddingHorizontal: 8, borderRadius: 999, fontSize: 12, fontWeight: '700', alignSelf: 'flex-start' },
  statCard: { backgroundColor: '#ffffff', borderWidth: 1, borderColor: '#e5e7eb', borderRadius: 8, paddingVertical: 8, paddingHorizontal: 12, minWidth: 100, alignItems: 'center' },
  statTitle: { fontSize: 12, color: '#334155' },
  statValue: { fontSize: 14, color: '#0f172a', fontWeight: '700' },
});

export default InvoiceGeneratorScreen;
